import { useState, useEffect, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Home, ClipboardList, MapPin, CreditCard, Music, MessageSquare, 
  Calendar, FileText, DollarSign, Download, Send, Mail, Plus, Trash2, Check, 
  Clock, Edit, Save, X, Upload, File, Eye, EyeOff, Settings, Lock,
  Image as ImageIcon, ChevronRight, Phone, Globe, Menu, Search, Building2,
  ArrowRight, Printer, ChevronDown, ChevronUp, Sun, Moon, Key, GripVertical, Loader2, Sparkles,
  Users, ThumbsUp, Copy, ExternalLink, XCircle, LayoutGrid, ListOrdered, PartyPopper
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { QRCodeCanvas } from 'qrcode.react';
import SeatingChartEditor from '@/components/SeatingChartEditor';
import { QuickStat, BentoCard, SectionHeader, InfoItem, EmptyState, PaymentScheduleItem } from '@/components/client-portal/PortalComponents';
import EventExperienceSection from '@/components/client-portal/EventExperienceSection';
import { PlaylistSongRow, SortablePlaylistSong, SortableRepeaterRow, SortableRepeater } from '@/components/client-portal/PlaylistComponents';
import { renderFormField } from '@/components/client-portal/FormFieldRenderer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ContactInfoCard({ booking, token, setBooking }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState(booking?.client_email || '');
  const [phone, setPhone] = useState(booking?.client_phone || '');

  useEffect(() => {
    setEmail(booking?.client_email || '');
    setPhone(booking?.client_phone || '');
  }, [booking?.client_email, booking?.client_phone]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/client-portal/${token}/contact-info`, {
        client_email: email,
        client_phone: phone,
      });
      setBooking(prev => ({ ...prev, client_email: email, client_phone: phone }));
      setEditing(false);
      toast.success('Contact info updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl p-5 space-y-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-white">Contact Information</h3>
        </div>
        {!editing ? (
          <button onClick={() => setEditing(true)} className="text-xs text-white/40 hover:text-white flex items-center gap-1" data-testid="edit-contact-btn">
            <Edit className="w-3.5 h-3.5" /> Edit
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button onClick={() => { setEditing(false); setEmail(booking?.client_email || ''); setPhone(booking?.client_phone || ''); }} className="text-xs text-white/40 hover:text-white">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="text-xs text-blue-400 hover:text-blue-300 font-medium flex items-center gap-1" data-testid="save-contact-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
            </button>
          </div>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-xs text-white/40">Email Address</label>
          {editing ? (
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-1 focus:ring-blue-500"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              data-testid="contact-email-input"
            />
          ) : (
            <p className="text-sm text-white">{booking?.client_email || '—'}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <label className="text-xs text-white/40">Phone Number</label>
          {editing ? (
            <input
              type="tel"
              value={phone}
              onChange={e => setPhone(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm text-white outline-none focus:ring-1 focus:ring-blue-500"
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
              data-testid="contact-phone-input"
            />
          ) : (
            <p className="text-sm text-white">{booking?.client_phone || '—'}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ClientPortalV2() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('dashboard');
  
  // Edit lockout state
  const [editLockout, setEditLockout] = useState({ is_locked: false, forms_locked: false, timeline_locked: false, playlists_locked: false, message: '', last_edit_date: null });
  
  // Multiple bookings state
  const [allBookings, setAllBookings] = useState([]);
  const [currentBookingId, setCurrentBookingId] = useState(null);
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Branding state
  const [logoError, setLogoError] = useState(false);
  
  // Venue state
  const [venueInfo, setVenueInfo] = useState(null);
  const [venueChangeRequests, setVenueChangeRequests] = useState([]);
  const [showVenueChangeDialog, setShowVenueChangeDialog] = useState(false);
  const [venueChangeForm, setVenueChangeForm] = useState({ name: '', address: '', city: '', state: '', zip_code: '', notes: '' });
  const [submittingVenueChange, setSubmittingVenueChange] = useState(false);
  const [showVenueImageUpload, setShowVenueImageUpload] = useState(false);
  const [venueImage, setVenueImage] = useState(null);
  const [uploadingVenueImage, setUploadingVenueImage] = useState(false);
  const venueImageInputRef = useRef(null);
  const [venueSearchResults, setVenueSearchResults] = useState([]);
  const [searchingVenues, setSearchingVenues] = useState(false);
  const [showVenueSearchResults, setShowVenueSearchResults] = useState(false);
  
  // Message state
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  
  // Form state
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [formResponses, setFormResponses] = useState({});
  const [submittingForm, setSubmittingForm] = useState(false);
  const [currentFormLocked, setCurrentFormLocked] = useState(false);
  const [autoSaveStatus, setAutoSaveStatus] = useState(''); // '', 'saving', 'saved', 'error'
  const autoSaveTimerRef = useRef(null);
  const formResponsesRef = useRef(formResponses);
  const selectedFormRef = useRef(selectedForm);
  
  // Playlist state
  const [showPlaylistDialog, setShowPlaylistDialog] = useState(false);
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [playlistName, setPlaylistName] = useState('');
  const [playlistSongs, setPlaylistSongs] = useState([{ title: '', artist: '', designation: 'must_play', comment: '' }]);
  const [savingPlaylist, setSavingPlaylist] = useState(false);
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const [importingSpotify, setImportingSpotify] = useState(false);
  const [playlistAutoSaveStatus, setPlaylistAutoSaveStatus] = useState('');
  const playlistAutoSaveTimerRef = useRef(null);
  const playlistSongsRef = useRef(playlistSongs);
  const playlistNameRef = useRef(playlistName);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [playInOrder, setPlayInOrder] = useState(false);
  const playInOrderRef = useRef(playInOrder);
  
  // Drag-and-drop sensors for playlist song reordering
  const playlistDndSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  // Apple Music import state
  const [showAppleMusicImport, setShowAppleMusicImport] = useState(false);
  const [appleMusicUrl, setAppleMusicUrl] = useState('');
  const [importingAppleMusic, setImportingAppleMusic] = useState(false);
  const [appleMusicImportResult, setAppleMusicImportResult] = useState(null);
  const [appleMusicTargetPlaylist, setAppleMusicTargetPlaylist] = useState('new');

  // YouTube import state
  const [showYouTubeImport, setShowYouTubeImport] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [importingYouTube, setImportingYouTube] = useState(false);
  const [youtubeTargetPlaylist, setYoutubeTargetPlaylist] = useState('new');
  const [youtubeSearchQuery, setYoutubeSearchQuery] = useState('');
  const [youtubeSearchResults, setYoutubeSearchResults] = useState([]);
  const [searchingYouTube, setSearchingYouTube] = useState(false);
  const [showYouTubeSearch, setShowYouTubeSearch] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const qrRef = useRef(null);
  
  // Vendor email state
  const [showVendorEmailDialog, setShowVendorEmailDialog] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [vendorEmailMessage, setVendorEmailMessage] = useState('');
  const [sendingVendorEmail, setSendingVendorEmail] = useState(false);
  
  // Add vendor state
  const [showAddVendorDialog, setShowAddVendorDialog] = useState(false);
  const [newVendor, setNewVendor] = useState({
    name: '',
    category: 'Photographer',
    contact_name: '',
    email: '',
    phone: '',
    notes: ''
  });
  const [addingVendor, setAddingVendor] = useState(false);
  const [playlistToDelete, setPlaylistToDelete] = useState(null);
  const [deletingPlaylist, setDeletingPlaylist] = useState(false);
  const editingPlaylistRef = useRef(editingPlaylist);
  
  // AI Playlist Generator state
  const [showAIPlaylistDialog, setShowAIPlaylistDialog] = useState(false);
  const [aiPlaylistCriteria, setAiPlaylistCriteria] = useState('');
  const [aiPlaylistName, setAiPlaylistName] = useState('');
  const [generatingAIPlaylist, setGeneratingAIPlaylist] = useState(false);
  
  // Guest Request System state
  const [guestRequestSettings, setGuestRequestSettings] = useState({ enabled: false, password: '', link_code: '' });
  const [guestRequests, setGuestRequests] = useState([]);
  const [showGuestRequestSetup, setShowGuestRequestSetup] = useState(false);
  const [savingGuestSettings, setSavingGuestSettings] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [requestToApprove, setRequestToApprove] = useState(null);
  const [selectedApprovePlaylist, setSelectedApprovePlaylist] = useState('');
  
  // Song search state
  const [songSearchQuery, setSongSearchQuery] = useState('');
  const [songSearchResults, setSongSearchResults] = useState([]);
  const [searchingSongs, setSearchingSongs] = useState(false);
  const [searchingSongIndex, setSearchingSongIndex] = useState(null);
  const [showSongSearchResults, setShowSongSearchResults] = useState(false);
  
  // Add Song modal state
  const [showAddSongModal, setShowAddSongModal] = useState(false);
  const [newSong, setNewSong] = useState({ title: '', artist: '', designation: '', comment: '' });
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [modalSearchResults, setModalSearchResults] = useState([]);
  const [modalSearching, setModalSearching] = useState(false);
  const [showModalSearchResults, setShowModalSearchResults] = useState(false);
  
  // Timeline state
  const [showTimelineDialog, setShowTimelineDialog] = useState(false);
  const [newTimelineEvent, setNewTimelineEvent] = useState({ time: '', title: '', description: '' });
  const [savingTimeline, setSavingTimeline] = useState(false);
  const [timelineAutoSaveStatus, setTimelineAutoSaveStatus] = useState('');
  const timelineAutoSaveTimerRef = useRef(null);
  const [editingTimelineEvent, setEditingTimelineEvent] = useState(null);
  const [editingTimelineData, setEditingTimelineData] = useState({ time: '', title: '', description: '' });
  const editingTimelineDataRef = useRef(editingTimelineData);
  const editingTimelineEventRef = useRef(editingTimelineEvent);
  
  // Payment state
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Document state
  const [showDocUploadDialog, setShowDocUploadDialog] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docNote, setDocNote] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);
  
  // Email state
  const [emailCommunications, setEmailCommunications] = useState([]);
  const [showComposeEmailDialog, setShowComposeEmailDialog] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [emailForm, setEmailForm] = useState({ subject: '', body: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [expandedEmails, setExpandedEmails] = useState({});

  // Contract signing state
  const [showSignContractDialog, setShowSignContractDialog] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signingContract, setSigningContract] = useState(false);
  const [showViewContractDialog, setShowViewContractDialog] = useState(false);

  // Invoice state
  const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(false);

  // Add-on state
  const [availableAddons, setAvailableAddons] = useState([]);
  const [pendingAddonRequests, setPendingAddonRequests] = useState([]);
  const [addonRequestHistory, setAddonRequestHistory] = useState([]);
  const [requestingAddon, setRequestingAddon] = useState(null);
  const [addonRequestNotes, setAddonRequestNotes] = useState('');
  const [submittingAddonRequest, setSubmittingAddonRequest] = useState(false);

  // Client settings state
  const [clientTheme, setClientTheme] = useState('dark');
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', new: '', confirm: '' });
  const [changingPassword, setChangingPassword] = useState(false);

  // Theme - Use DJ's branding
  const djPrimaryColor = data?.settings?.primary_color || '#06b6d4';
  const djSecondaryColor = data?.settings?.secondary_color || '#8b5cf6';
  
  // Base URL helper - uses custom domain if configured
  const getBaseUrl = () => {
    const customDomain = data?.settings?.custom_domain;
    if (customDomain) return `https://${customDomain}`;
    return window.location.origin;
  };

  // Date formatting helper - handles timezone correctly
  const formatDate = (dateInput, showDay = false) => {
    if (!dateInput) return '';
    try {
      let date;
      if (typeof dateInput === 'string') {
        // For date-only strings (YYYY-MM-DD), parse as local date to avoid timezone shift
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
          const [year, month, day] = dateInput.split('-').map(Number);
          date = new Date(year, month - 1, day);
        } else {
          date = new Date(dateInput);
        }
      } else {
        date = dateInput;
      }
      if (isNaN(date.getTime())) return dateInput;
      const format = data?.settings?.date_format || 'MM/DD/YYYY';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const monthShort = date.toLocaleString('en-US', { month: 'short' });
      const day = String(date.getDate()).padStart(2, '0');
      const weekday = date.toLocaleString('en-US', { weekday: 'short' });
      let formatted;
      switch (format) {
        case 'MM/DD/YYYY': formatted = `${month}/${day}/${year}`; break;
        case 'DD/MM/YYYY': formatted = `${day}/${month}/${year}`; break;
        case 'YYYY-MM-DD': formatted = `${year}-${month}-${day}`; break;
        case 'MMM DD, YYYY': formatted = `${monthShort} ${day}, ${year}`; break;
        default: formatted = `${month}/${day}/${year}`;
      }
      return showDay ? `${weekday} - ${formatted}` : formatted;
    } catch {
      return dateInput;
    }
  };

  const formatTime = (timeInput) => {
    if (!timeInput) return '';
    try {
      const timeFormat = data?.settings?.time_format || '12h';
      if (typeof timeInput === 'string' && (timeInput.includes('AM') || timeInput.includes('PM'))) {
        if (timeFormat === '24h') {
          const match = timeInput.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
          if (match) {
            let hours = parseInt(match[1], 10);
            const minutes = match[2];
            const period = match[3].toUpperCase();
            if (period === 'PM' && hours !== 12) hours += 12;
            if (period === 'AM' && hours === 12) hours = 0;
            return `${hours.toString().padStart(2, '0')}:${minutes}`;
          }
        }
        return timeInput;
      }
      let hours, minutes;
      if (typeof timeInput === 'string' && timeInput.includes(':')) {
        const parts = timeInput.split(':');
        hours = parseInt(parts[0], 10);
        minutes = parts[1];
      } else return timeInput;
      if (timeFormat === '24h') return `${hours.toString().padStart(2, '0')}:${minutes}`;
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      return `${displayHours}:${minutes} ${period}`;
    } catch {
      return timeInput;
    }
  };

  const getCountdown = () => {
    if (!data?.booking?.event_date) return null;
    const eventDate = new Date(data.booking.event_date);
    const today = new Date();
    const diffTime = eventDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Get display status for booking
  const getBookingDisplayStatus = (bookingData) => {
    if (!bookingData) return null;
    const status = bookingData.status?.toLowerCase();
    const eventDate = new Date(bookingData.event_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    const isPast = eventDate < today;

    if (status === 'cancelled') {
      return { label: 'Cancelled', color: 'bg-red-500/20 text-red-400' };
    }
    if (isPast) {
      return { label: 'Past', color: 'bg-gray-500/20 text-gray-400' };
    }
    if (status === 'confirmed') {
      return { label: 'Booked', color: 'bg-green-500/20 text-green-400' };
    }
    if (status === 'pending') {
      return { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400' };
    }
    if (status === 'completed') {
      return { label: 'Completed', color: 'bg-blue-500/20 text-blue-400' };
    }
    return { label: status || 'Unknown', color: 'bg-gray-500/20 text-gray-400' };
  };

  // Get outstanding requirements for pending bookings
  const getOutstandingRequirements = (bookingData, contractsData) => {
    if (!bookingData || bookingData.status?.toLowerCase() !== 'pending') return [];
    
    const requirements = [];
    
    // Check if contract needs signing
    const hasContracts = contractsData && contractsData.length > 0;
    if (hasContracts) {
      const needsSignature = contractsData.some(c => 
        c.status !== 'client_signed' && c.status !== 'fully_signed'
      );
      if (needsSignature) {
        requirements.push({ type: 'contract', label: 'Sign Contract', icon: 'FileText' });
      }
    }
    
    // Check if payment needs to be made
    const paidAmount = parseFloat(bookingData.paid_amount || 0);
    const paymentSchedule = bookingData.payment_schedule || [];
    
    // Find retainer amount
    const retainerPayment = paymentSchedule.find(p => p.payment_type === 'retainer');
    const retainerAmount = retainerPayment ? parseFloat(retainerPayment.amount || 0) : 
      parseFloat(bookingData.retainer_amount || bookingData.deposit_amount || 0);
    
    // Find pay-in-full amount
    const payInFull = paymentSchedule.find(p => p.payment_type === 'pay_in_full');
    const payInFullAmount = payInFull ? parseFloat(payInFull.amount || 0) : 0;
    
    // Check if minimum payment is satisfied
    const needsPayment = (retainerAmount > 0 && paidAmount < retainerAmount) ||
      (payInFullAmount > 0 && paidAmount < payInFullAmount && paidAmount < retainerAmount);
    
    if (needsPayment || (retainerAmount === 0 && payInFullAmount === 0 && paidAmount === 0)) {
      requirements.push({ type: 'payment', label: 'Make Payment', icon: 'CreditCard' });
    }
    
    return requirements;
  };

  useEffect(() => {
    fetchPortalData();
    fetchAllBookings();
    fetchCommunications();
    fetchAvailableAddons();
    fetchAddonRequests();
    fetchVenueInfo();
    fetchVenueChangeRequests();
    fetchClientTheme();
  }, [token]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [data?.messages]);

  // Apply client theme to the portal
  useEffect(() => {
    document.documentElement.classList.remove('client-theme-light', 'client-theme-dark');
    document.documentElement.classList.add(`client-theme-${clientTheme}`);
  }, [clientTheme]);

  const fetchClientTheme = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}/theme`);
      setClientTheme(response.data.theme || 'dark');
    } catch {}
  };

  const handleThemeChange = async (newTheme) => {
    try {
      await axios.put(`${API}/client-portal/${token}/theme`, { theme: newTheme });
      setClientTheme(newTheme);
      toast.success(`Theme changed to ${newTheme} mode`);
    } catch {
      toast.error('Failed to update theme');
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current) {
      toast.error('Please enter your current password');
      return;
    }
    if (passwordForm.new.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('New passwords do not match');
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(`${API}/client-portal/${token}/change-password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.new
      });
      toast.success('Password changed successfully!');
      setShowChangePasswordDialog(false);
      setPasswordForm({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const fetchPortalData = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}`);
      if (response.data.maintenance_mode) {
        setData({ maintenance_mode: true, maintenance_message: response.data.maintenance_message });
        setLoading(false);
        return;
      }
      setData(response.data);
      // Set edit lockout status
      if (response.data.edit_lockout) {
        setEditLockout(response.data.edit_lockout);
      }
      const paymentStatus = searchParams.get('payment');
      if (paymentStatus === 'success') {
        try {
          const verifyResponse = await axios.post(`${API}/client-portal/${token}/verify-payment`);
          if (verifyResponse.data.verified) {
            if (verifyResponse.data.booking_auto_confirmed) {
              toast.success(`Payment recorded and booking confirmed! 🎉`, { duration: 5000 });
            } else {
              toast.success(`Payment of $${verifyResponse.data.amount_verified.toFixed(2)} recorded!`);
            }
            setTimeout(() => { window.location.href = window.location.pathname; }, 2000);
          } else toast.success('Payment successful!');
        } catch {
          toast.success('Payment successful!');
        }
      } else if (paymentStatus === 'cancelled') toast.info('Payment was cancelled.');
      fetchPaymentSettings();
    } catch {
      toast.error('Portal not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBookings = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}/all-bookings`);
      setAllBookings(response.data.bookings || []);
      setCurrentBookingId(response.data.current_booking_id);
    } catch {}
  };

  const fetchCommunications = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}/communications`);
      setEmailCommunications(response.data || []);
    } catch {}
  };

  const fetchPaymentSettings = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}/payment-settings`);
      setPaymentSettings(response.data);
      if (response.data.balance > 0) {
        const defaultAmount = response.data.deposit_met 
          ? response.data.balance 
          : Math.min(response.data.minimum_payment, response.data.balance);
        setPaymentAmount(defaultAmount.toFixed(2));
      }
    } catch {}
  };

  const fetchAvailableAddons = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}/available-addons`);
      setAvailableAddons(response.data.available_addons || []);
      setPendingAddonRequests(response.data.pending_addon_ids || []);
    } catch {}
  };

  const fetchAddonRequests = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}/addon-requests`);
      setAddonRequestHistory(response.data || []);
    } catch {}
  };

  const fetchVenueInfo = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}/venue-info`);
      setVenueInfo(response.data);
    } catch {}
  };

  const fetchVenueChangeRequests = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}/venue-change-requests`);
      setVenueChangeRequests(response.data || []);
    } catch {}
  };

  const switchToBooking = (bookingToken) => {
    if (bookingToken !== token) window.location.href = `/portal/${bookingToken}`;
    setMobileMenuOpen(false);
  };

  // Venue handlers
  const handleVenueImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { toast.error('Image must be less than 5MB'); return; }
      if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
      setVenueImage(file);
    }
  };

  const handleUploadVenueImage = async () => {
    if (!venueImage) { toast.error('Please select an image'); return; }
    setUploadingVenueImage(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        await axios.put(`${API}/client-portal/${token}/venue-image`, { image_data: e.target.result });
        toast.success('Venue image uploaded!');
        setShowVenueImageUpload(false);
        setVenueImage(null);
        fetchVenueInfo();
      };
      reader.readAsDataURL(venueImage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingVenueImage(false);
    }
  };

  const handleSubmitVenueChange = async () => {
    if (!venueChangeForm.name.trim()) { toast.error('Please enter a venue name'); return; }
    if (!venueChangeForm.address.trim()) { toast.error('Please enter a venue address'); return; }
    setSubmittingVenueChange(true);
    try {
      // Combine address parts for full address
      const fullAddress = [
        venueChangeForm.address,
        venueChangeForm.city,
        venueChangeForm.state,
        venueChangeForm.zip_code
      ].filter(Boolean).join(', ');
      
      await axios.post(`${API}/client-portal/${token}/venue-change-requests`, {
        new_venue_name: venueChangeForm.name,
        new_venue_address: fullAddress,
        new_venue_city: venueChangeForm.city,
        new_venue_state: venueChangeForm.state,
        new_venue_zip_code: venueChangeForm.zip_code,
        client_notes: venueChangeForm.notes
      });
      toast.success('Venue change request submitted!');
      setShowVenueChangeDialog(false);
      setVenueChangeForm({ name: '', address: '', city: '', state: '', zip_code: '', notes: '' });
      fetchVenueChangeRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingVenueChange(false);
    }
  };

  // Venue search handler
  const searchVenues = async (query) => {
    if (!query || query.length < 2) { setVenueSearchResults([]); setShowVenueSearchResults(false); return; }
    setSearchingVenues(true);
    setShowVenueSearchResults(true);
    try {
      const response = await axios.get(`${API}/client-portal/${token}/search-venues`, { params: { query } });
      setVenueSearchResults(response.data || []);
    } catch {
      setVenueSearchResults([]);
    } finally {
      setSearchingVenues(false);
    }
  };

  const selectVenueFromSearch = (venue) => {
    setVenueChangeForm({
      name: venue.name,
      address: venue.address || '',
      city: venue.city || '',
      state: venue.state || '',
      zip_code: venue.zip_code || '',
      notes: venueChangeForm.notes
    });
    setShowVenueSearchResults(false);
    setVenueSearchResults([]);
  };

  // Payment Handler
  const handleMakePayment = async () => {
    const amount = parseFloat(paymentAmount);
    if (!paymentAmount || amount <= 0) { toast.error('Please enter a valid amount'); return; }
    if (paymentSettings && !paymentSettings.deposit_met && amount < paymentSettings.minimum_payment) {
      toast.error(`Minimum payment of $${paymentSettings.minimum_payment.toFixed(2)} required`);
      return;
    }
    if (paymentSettings && amount > paymentSettings.balance) {
      toast.error(`Amount cannot exceed $${paymentSettings.balance.toFixed(2)}`);
      return;
    }
    setProcessingPayment(true);
    try {
      const response = await axios.post(`${API}/client-portal/${token}/create-payment`, {
        amount, description: window.location.origin
      });
      if (response.data.checkout_url) window.location.href = response.data.checkout_url;
      else setShowPaymentDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Message handlers
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    setSendingMessage(true);
    try {
      await axios.post(`${API}/client-portal/${token}/messages?content=${encodeURIComponent(newMessage)}&send_email=false`);
      setNewMessage('');
      fetchPortalData();
      toast.success('Message sent!');
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Form handlers
  const openFormDialog = (form, locked = false) => {
    setSelectedForm(form);
    setCurrentFormLocked(locked);
    const initialResponses = {};
    form.fields?.forEach(field => {
      if (field.type === 'repeater') initialResponses[field.name] = [{ col1: '', col2: '' }];
      else if (field.type === 'repeater3') initialResponses[field.name] = [{ col1: '', col2: '', col3: '' }];
      else if (field.type === 'columns2') initialResponses[field.name] = { col1: '', col2: '' };
      else if (field.type === 'columns3') initialResponses[field.name] = { col1: '', col2: '', col3: '' };
      else if (field.type === 'checkbox') initialResponses[field.name] = false;
      else if (field.type === 'checkbox_conditional') initialResponses[field.name] = { checked: false, conditionalValue: '' };
      else if (field.type !== 'section' && field.type !== 'comment') initialResponses[field.name] = '';
    });
    const existingResponse = data.form_responses?.find(r => r.form_id === form.id);
    setFormResponses(existingResponse?.responses || initialResponses);
    setShowFormDialog(true);
  };

  const handleFormInputChange = (fieldName, value) => {
    setFormResponses(prev => ({ ...prev, [fieldName]: value }));
    // Trigger auto-save
    triggerAutoSave();
  };

  const handleRepeaterChange = (fieldName, index, column, value, is3Col = false) => {
    setFormResponses(prev => {
      const rows = [...(prev[fieldName] || [])];
      if (!rows[index]) rows[index] = is3Col ? { col1: '', col2: '', col3: '' } : { col1: '', col2: '' };
      rows[index][column] = value;
      return { ...prev, [fieldName]: rows };
    });
    // Trigger auto-save
    triggerAutoSave();
  };

  const addRepeaterRow = (fieldName, is3Col = false) => {
    setFormResponses(prev => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), is3Col ? { col1: '', col2: '', col3: '' } : { col1: '', col2: '' }]
    }));
    // Trigger auto-save
    triggerAutoSave();
  };

  const removeRepeaterRow = (fieldName, index) => {
    setFormResponses(prev => ({
      ...prev,
      [fieldName]: (prev[fieldName] || []).filter((_, i) => i !== index)
    }));
    // Trigger auto-save
    triggerAutoSave();
  };

  const reorderRepeaterRows = (fieldName, oldIndex, newIndex) => {
    setFormResponses(prev => {
      const rows = [...(prev[fieldName] || [])];
      const [removed] = rows.splice(oldIndex, 1);
      rows.splice(newIndex, 0, removed);
      return { ...prev, [fieldName]: rows };
    });
    // Trigger auto-save
    triggerAutoSave();
  };

  const handleColumnChange = (fieldName, column, value) => {
    setFormResponses(prev => ({
      ...prev,
      [fieldName]: { ...(prev[fieldName] || {}), [column]: value }
    }));
    // Trigger auto-save
    triggerAutoSave();
  };

  const submitFormResponse = async () => {
    if (!selectedForm || !data.booking) return;
    setSubmittingForm(true);
    // Clear any pending auto-save
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = null;
    }
    try {
      await axios.post(`${API}/client-portal/${token}/form-response`, {
        form_id: selectedForm.id,
        booking_id: data.booking.id,
        responses: formResponses
      });
      
      // Update local data state to reflect the saved responses
      setData(prevData => {
        if (!prevData) return prevData;
        const existingResponses = prevData.form_responses || [];
        const existingIndex = existingResponses.findIndex(r => r.form_id === selectedForm.id);
        const updatedResponse = {
          form_id: selectedForm.id,
          booking_id: data.booking.id,
          responses: formResponses,
          last_edited_by: 'client',
          last_edited_at: new Date().toISOString()
        };
        
        let newResponses;
        if (existingIndex >= 0) {
          newResponses = [...existingResponses];
          newResponses[existingIndex] = { ...existingResponses[existingIndex], ...updatedResponse };
        } else {
          newResponses = [...existingResponses, updatedResponse];
        }
        
        return { ...prevData, form_responses: newResponses };
      });
      
      toast.success('Form saved!');
      setAutoSaveStatus('saved');
      setShowFormDialog(false);
    } catch {
      toast.error('Failed to save form');
      setAutoSaveStatus('error');
    } finally {
      setSubmittingForm(false);
    }
  };

  // Auto-save function with debounce
  const triggerAutoSave = () => {
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    
    setAutoSaveStatus(''); // Reset status while typing
    
    // Set new timer - save after 1.5 seconds of no changes
    autoSaveTimerRef.current = setTimeout(async () => {
      const currentForm = selectedFormRef.current;
      const responsesToSave = formResponsesRef.current;
      
      if (!currentForm || !data?.booking || currentFormLocked) return;
      
      setAutoSaveStatus('saving');
      try {
        await axios.post(`${API}/client-portal/${token}/form-response`, {
          form_id: currentForm.id,
          booking_id: data.booking.id,
          responses: responsesToSave
        });
        
        // Update local data state to reflect the saved responses
        setData(prevData => {
          if (!prevData) return prevData;
          const existingResponses = prevData.form_responses || [];
          const existingIndex = existingResponses.findIndex(r => r.form_id === currentForm.id);
          const updatedResponse = {
            form_id: currentForm.id,
            booking_id: data.booking.id,
            responses: responsesToSave,
            last_edited_by: 'client',
            last_edited_at: new Date().toISOString()
          };
          
          let newResponses;
          if (existingIndex >= 0) {
            newResponses = [...existingResponses];
            newResponses[existingIndex] = { ...existingResponses[existingIndex], ...updatedResponse };
          } else {
            newResponses = [...existingResponses, updatedResponse];
          }
          
          return { ...prevData, form_responses: newResponses };
        });
        
        setAutoSaveStatus('saved');
        // Clear "saved" status after 3 seconds
        setTimeout(() => setAutoSaveStatus(''), 3000);
      } catch {
        setAutoSaveStatus('error');
        // Clear error status after 5 seconds
        setTimeout(() => setAutoSaveStatus(''), 5000);
      }
    }, 1500);
  };

  // Keep ref in sync with state for auto-save
  useEffect(() => {
    formResponsesRef.current = formResponses;
  }, [formResponses]);

  // Keep selectedForm ref in sync
  useEffect(() => {
    selectedFormRef.current = selectedForm;
  }, [selectedForm]);

  // Keep playlist refs in sync
  useEffect(() => {
    playlistSongsRef.current = playlistSongs;
  }, [playlistSongs]);

  useEffect(() => {
    playlistNameRef.current = playlistName;
  }, [playlistName]);

  useEffect(() => {
    playInOrderRef.current = playInOrder;
  }, [playInOrder]);


  useEffect(() => {
    editingPlaylistRef.current = editingPlaylist;
  }, [editingPlaylist]);

  // Keep timeline editing refs in sync
  useEffect(() => {
    editingTimelineDataRef.current = editingTimelineData;
  }, [editingTimelineData]);

  useEffect(() => {
    editingTimelineEventRef.current = editingTimelineEvent;
  }, [editingTimelineEvent]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
      if (playlistAutoSaveTimerRef.current) clearTimeout(playlistAutoSaveTimerRef.current);
      if (timelineAutoSaveTimerRef.current) clearTimeout(timelineAutoSaveTimerRef.current);
    };
  }, []);

  // Check if form is locked
  const isFormLocked = (form) => {
    // Per-booking forms_locked override takes priority over everything
    if (editLockout.forms_locked_override === true) return true;
    if (editLockout.forms_locked_override === false) return false;
    // No override set — check per-form lockout settings
    if (!form.lockout_days_before_event && !form.lockout_days_after_event) return false;
    const eventDate = new Date(data?.booking?.event_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);
    const daysUntilEvent = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
    const daysAfterEvent = Math.ceil((today - eventDate) / (1000 * 60 * 60 * 24));
    if (form.lockout_days_before_event !== null && form.lockout_days_before_event !== undefined) {
      if (daysUntilEvent >= 0 && daysUntilEvent <= form.lockout_days_before_event) return true;
    }
    if (form.lockout_days_after_event !== null && form.lockout_days_after_event !== undefined) {
      if (daysAfterEvent > 0 && daysAfterEvent <= form.lockout_days_after_event) return true;
      if (form.lockout_days_after_event === 0 && daysAfterEvent >= 0) return true;
    }
    return false;
  };

  // Payment schedule helpers
  const isPaymentMade = (description, amount) => {
    if (!payments || payments.length === 0) return false;
    // Check if a payment exists that matches this scheduled payment
    // This is a simple check - could be enhanced with explicit linking
    const paidTotal = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
    const retainerAmount = data?.booking?.retainer_amount || data?.booking?.deposit_amount || 0;
    
    if (description === 'retainer' && retainerAmount > 0) {
      return paidTotal >= retainerAmount;
    }
    
    // Check if total paid covers up to this payment in the schedule
    const schedule = data?.booking?.payment_schedule || [];
    let runningTotal = retainerAmount;
    
    for (const sched of schedule) {
      runningTotal += parseFloat(sched.amount) || 0;
      if (sched.description === description || (parseFloat(sched.amount) === amount)) {
        return paidTotal >= runningTotal;
      }
    }
    
    return false;
  };

  const handleScheduledPayment = (amount, description, allowPartial = false) => {
    if (allowPartial) {
      // For payments that allow custom amounts, pre-fill but allow editing
      setPaymentAmount(amount.toFixed(2));
    } else {
      // For fixed payments (retainer, pay in full), set exact amount
      setPaymentAmount(amount.toFixed(2));
    }
    setShowPaymentDialog(true);
  };

  // Timeline helpers
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    if (timeStr.includes(':') && !timeStr.includes(' ')) {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    }
    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
    if (!match) return 0;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3]?.toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  const sortedTimelineEvents = (events) => {
    if (!events || !Array.isArray(events)) return [];
    return [...events].sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
  };

  const openTimelineDialog = () => {
    setNewTimelineEvent({ time: '', title: '', description: '' });
    setShowTimelineDialog(true);
  };

  const saveTimelineEvent = async () => {
    if (!newTimelineEvent.time.trim() || !newTimelineEvent.title.trim()) {
      toast.error('Please enter time and title');
      return;
    }
    setSavingTimeline(true);
    try {
      await axios.post(`${API}/client-portal/${token}/timeline/events`, newTimelineEvent);
      toast.success('Event added!');
      setShowTimelineDialog(false);
      fetchPortalData();
    } catch {
      toast.error('Failed to add event');
    } finally {
      setSavingTimeline(false);
    }
  };

  const deleteTimelineEvent = async (eventId) => {
    if (!window.confirm('Remove this event?')) return;
    try {
      await axios.delete(`${API}/client-portal/${token}/timeline/events/${eventId}`);
      toast.success('Event removed');
      fetchPortalData();
    } catch {
      toast.error('Failed to remove event');
    }
  };

  // Start editing a timeline event
  const startEditingTimelineEvent = (event) => {
    setEditingTimelineEvent(event.id);
    setEditingTimelineData({
      time: event.time || '',
      title: event.title || '',
      description: event.description || ''
    });
    setTimelineAutoSaveStatus('');
  };

  // Cancel timeline editing
  const cancelEditingTimeline = () => {
    setEditingTimelineEvent(null);
    setEditingTimelineData({ time: '', title: '', description: '' });
    setTimelineAutoSaveStatus('');
    if (timelineAutoSaveTimerRef.current) {
      clearTimeout(timelineAutoSaveTimerRef.current);
      timelineAutoSaveTimerRef.current = null;
    }
  };

  // Trigger auto-save for timeline event
  const triggerTimelineAutoSave = () => {
    if (timelineAutoSaveTimerRef.current) {
      clearTimeout(timelineAutoSaveTimerRef.current);
    }
    setTimelineAutoSaveStatus('');
    
    timelineAutoSaveTimerRef.current = setTimeout(async () => {
      const eventId = editingTimelineEventRef.current;
      const eventData = editingTimelineDataRef.current;
      
      if (!eventId || !eventData.time.trim() || !eventData.title.trim()) return;
      
      setTimelineAutoSaveStatus('saving');
      try {
        await axios.put(`${API}/client-portal/${token}/timeline/events/${eventId}`, {
          time: eventData.time,
          title: eventData.title,
          description: eventData.description || ''
        });
        
        // Update local timeline data
        setTimeline(prevTimeline => {
          if (!prevTimeline) return prevTimeline;
          const events = prevTimeline.events || [];
          const idx = events.findIndex(e => e.id === eventId);
          if (idx >= 0) {
            const updated = [...events];
            updated[idx] = { ...updated[idx], ...eventData };
            return { ...prevTimeline, events: updated };
          }
          return prevTimeline;
        });
        
        setTimelineAutoSaveStatus('saved');
        setTimeout(() => setTimelineAutoSaveStatus(''), 3000);
      } catch {
        setTimelineAutoSaveStatus('error');
        setTimeout(() => setTimelineAutoSaveStatus(''), 5000);
      }
    }, 1500);
  };

  // Save timeline event and close editor
  const saveTimelineEventEdit = async () => {
    if (!editingTimelineEvent) return;
    
    // Clear auto-save timer
    if (timelineAutoSaveTimerRef.current) {
      clearTimeout(timelineAutoSaveTimerRef.current);
      timelineAutoSaveTimerRef.current = null;
    }
    
    if (!editingTimelineData.time.trim() || !editingTimelineData.title.trim()) {
      toast.error('Please enter time and title');
      return;
    }
    
    setTimelineAutoSaveStatus('saving');
    try {
      await axios.put(`${API}/client-portal/${token}/timeline/events/${editingTimelineEvent}`, {
        time: editingTimelineData.time,
        title: editingTimelineData.title,
        description: editingTimelineData.description || ''
      });
      toast.success('Event saved!');
      setEditingTimelineEvent(null);
      fetchPortalData();
    } catch {
      toast.error('Failed to save event');
      setTimelineAutoSaveStatus('error');
    }
  };

  // Playlist handlers
  const openPlaylistDialog = (playlist = null) => {
    if (playlist) {
      setEditingPlaylist(playlist);
      setPlaylistName(playlist.name);
      // Ensure songs have designation field
      const songsWithDesignation = (playlist.songs?.length > 0 ? playlist.songs : [{ title: '', artist: '', designation: 'must_play' }])
        .map(s => ({ ...s, designation: s.designation || 'must_play' }));
      setPlaylistSongs(songsWithDesignation);
      setSpotifyUrl(playlist.spotify_url || '');
      setPlayInOrder(playlist.play_in_order || false);
    } else {
      setEditingPlaylist(null);
      setPlaylistName('');
      setPlaylistSongs([{ title: '', artist: '', designation: 'must_play' }]);
      setSpotifyUrl('');
      setPlayInOrder(false);
    }
    setPlaylistAutoSaveStatus('');
    setShowPlaylistDialog(true);
  };

  // Trigger auto-save for playlist
  const triggerPlaylistAutoSave = () => {
    if (playlistAutoSaveTimerRef.current) {
      clearTimeout(playlistAutoSaveTimerRef.current);
    }
    setPlaylistAutoSaveStatus('');
    
    playlistAutoSaveTimerRef.current = setTimeout(async () => {
      const currentPlaylist = editingPlaylistRef.current;
      const currentName = playlistNameRef.current;
      const currentSongs = playlistSongsRef.current;
      
      // Only auto-save if editing an existing playlist
      if (!currentPlaylist || !currentName.trim()) return;
      
      const validSongs = currentSongs.filter(s => s.title.trim() || s.artist.trim());
      if (validSongs.length === 0) return;
      
      setPlaylistAutoSaveStatus('saving');
      try {
        await axios.put(`${API}/client-portal/${token}/playlists/${currentPlaylist.id}`, {
          name: currentName,
          songs: validSongs,
          play_in_order: playInOrderRef.current
        });
        
        // Update local data
        setData(prevData => {
          if (!prevData) return prevData;
          const playlists = prevData.playlists || [];
          const idx = playlists.findIndex(p => p.id === currentPlaylist.id);
          if (idx >= 0) {
            const updated = [...playlists];
            updated[idx] = { ...updated[idx], name: currentName, songs: validSongs, play_in_order: playInOrderRef.current };
            return { ...prevData, playlists: updated };
          }
          return prevData;
        });
        
        setPlaylistAutoSaveStatus('saved');
        setTimeout(() => setPlaylistAutoSaveStatus(''), 3000);
      } catch {
        setPlaylistAutoSaveStatus('error');
        setTimeout(() => setPlaylistAutoSaveStatus(''), 5000);
      }
    }, 1500);
  };

  const savePlaylist = async () => {
    if (!playlistName.trim()) { toast.error('Please enter a playlist name'); return; }
    const validSongs = playlistSongs.filter(s => s.title.trim() || s.artist.trim());
    if (validSongs.length === 0) { toast.error('Please add at least one song'); return; }
    
    // Clear auto-save timer
    if (playlistAutoSaveTimerRef.current) {
      clearTimeout(playlistAutoSaveTimerRef.current);
      playlistAutoSaveTimerRef.current = null;
    }
    
    setSavingPlaylist(true);
    try {
      if (editingPlaylist) {
        await axios.put(`${API}/client-portal/${token}/playlists/${editingPlaylist.id}`, {
          name: playlistName, songs: validSongs, play_in_order: playInOrder
        });
      } else {
        await axios.post(`${API}/client-portal/${token}/playlists`, {
          name: playlistName, songs: validSongs, play_in_order: playInOrder
        });
      }
      toast.success(editingPlaylist ? 'Playlist saved!' : 'Playlist created!');
      setShowPlaylistDialog(false);
      fetchPortalData();
    } catch {
      toast.error('Failed to save playlist');
    } finally {
      setSavingPlaylist(false);
    }
  };

  // Apple Music Import
  const handleAppleMusicImport = async () => {
    if (!appleMusicUrl.trim()) {
      toast.error('Please enter an Apple Music playlist URL');
      return;
    }
    
    if (!appleMusicUrl.includes('music.apple.com')) {
      toast.error('Please enter a valid Apple Music URL');
      return;
    }
    
    setImportingAppleMusic(true);
    setAppleMusicImportResult(null);
    
    try {
      const payload = {
        url: appleMusicUrl,
        playlist_id: appleMusicTargetPlaylist !== 'new' ? appleMusicTargetPlaylist : null
      };
      
      const response = await axios.post(`${API}/client-portal/${token}/import-apple-music`, payload);
      
      if (appleMusicTargetPlaylist !== 'new' && response.data.action === 'added_to_existing') {
        toast.success(`Added ${response.data.song_count} songs to playlist!`);
        setShowAppleMusicImport(false);
        setAppleMusicUrl('');
        setAppleMusicTargetPlaylist('new');
        fetchPortalData();
      } else {
        // Show preview for creating new playlist
        setAppleMusicImportResult(response.data);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to import playlist';
      toast.error(errorMsg);
    } finally {
      setImportingAppleMusic(false);
    }
  };

  const confirmAppleMusicImport = async () => {
    if (!appleMusicImportResult) return;
    
    setSavingPlaylist(true);
    try {
      await axios.post(`${API}/client-portal/${token}/playlists`, {
        name: appleMusicImportResult.playlist_name,
        songs: appleMusicImportResult.songs,
        play_in_order: true
      });
      toast.success(`Created playlist "${appleMusicImportResult.playlist_name}" with ${appleMusicImportResult.song_count} songs!`);
      setShowAppleMusicImport(false);
      setAppleMusicUrl('');
      setAppleMusicImportResult(null);
      setAppleMusicTargetPlaylist('new');
      fetchPortalData();
    } catch {
      toast.error('Failed to create playlist');
    } finally {
      setSavingPlaylist(false);
    }
  };

  // YouTube Import
  const handleYouTubeImport = async () => {
    if (!youtubeUrl.trim()) {
      toast.error('Please enter a YouTube playlist URL');
      return;
    }
    if (!youtubeUrl.includes('youtube.com') && !youtubeUrl.includes('youtu.be')) {
      toast.error('Please enter a valid YouTube URL');
      return;
    }
    setImportingYouTube(true);
    try {
      const payload = {
        youtube_url: youtubeUrl,
        playlist_id: youtubeTargetPlaylist !== 'new' ? youtubeTargetPlaylist : null
      };
      const response = await axios.post(`${API}/client-portal/${token}/youtube-import`, payload);
      
      if (youtubeTargetPlaylist !== 'new' && response.data.action === 'added_to_existing') {
        toast.success(`Added ${response.data.songs_imported} songs to "${response.data.playlist_name}"!`);
        setShowYouTubeImport(false);
        setYoutubeUrl('');
        setYoutubeTargetPlaylist('new');
      } else {
        toast.success(`Created playlist "${response.data.playlist_name}" with ${response.data.songs_imported} songs!`);
        setShowYouTubeImport(false);
        setYoutubeUrl('');
        setYoutubeTargetPlaylist('new');
      }
      fetchPortalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import YouTube playlist');
    } finally {
      setImportingYouTube(false);
    }
  };

  // YouTube Search
  const searchYouTube = async () => {
    if (!youtubeSearchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }
    setSearchingYouTube(true);
    try {
      const response = await axios.get(`${API}/client-portal/${token}/youtube-search`, {
        params: { query: youtubeSearchQuery, limit: 10 }
      });
      setYoutubeSearchResults(response.data.results || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSearchingYouTube(false);
    }
  };

  const addYouTubeSongToPlaylist = (song) => {
    const newSong = {
      title: song.title,
      artist: song.artist,
      youtube_url: song.youtube_url,
      designation: 'must_play',
      comment: ''
    };
    setPlaylistSongs(prev => [...prev, newSong]);
    toast.success(`Added "${song.title}" to playlist`);
    if (editingPlaylist) triggerPlaylistAutoSave();
  };

  const deletePlaylist = async (playlistId) => {
    // Find the playlist to show its name in the confirmation
    const playlist = playlists?.find(p => p.id === playlistId);
    setPlaylistToDelete(playlist || { id: playlistId, name: 'this playlist' });
    setShowDeleteConfirm(true);
  };

  const confirmDeletePlaylist = async () => {
    if (!playlistToDelete) return;
    
    setDeletingPlaylist(true);
    try {
      await axios.delete(`${API}/client-portal/${token}/playlists/${playlistToDelete.id}`);
      toast.success('Playlist deleted');
      setShowDeleteConfirm(false);
      setPlaylistToDelete(null);
      fetchPortalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete playlist');
    } finally {
      setDeletingPlaylist(false);
    }
  };

  const importFromSpotify = async () => {
    if (!spotifyUrl.trim()) { toast.error('Please enter a Spotify URL'); return; }
    setImportingSpotify(true);
    try {
      const response = await axios.post(`${API}/client-portal/${token}/spotify-import`, {
        spotify_url: spotifyUrl, playlist_name: playlistName || null
      });
      toast.success(`Imported ${response.data.songs_imported} songs!`);
      setShowPlaylistDialog(false);
      fetchPortalData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import');
    } finally {
      setImportingSpotify(false);
    }
  };

  // AI Playlist Generator
  const generateAIPlaylist = async () => {
    if (!aiPlaylistCriteria.trim()) {
      toast.error('Please describe what kind of music you want');
      return;
    }
    if (!aiPlaylistName.trim()) {
      toast.error('Please enter a playlist name');
      return;
    }
    
    setGeneratingAIPlaylist(true);
    try {
      const response = await axios.post(`${API}/client-portal/${token}/generate-ai-playlist`, {
        criteria: aiPlaylistCriteria,
        playlist_name: aiPlaylistName,
        num_songs: 50
      });
      
      toast.success(`Created playlist with ${response.data.songs_generated} songs!`);
      setShowAIPlaylistDialog(false);
      setAiPlaylistCriteria('');
      setAiPlaylistName('');
      fetchPortalData();
      
      // Open the newly created playlist for editing
      if (response.data.playlist) {
        setTimeout(() => {
          openPlaylistDialog(response.data.playlist);
        }, 500);
      }
    } catch (error) {
      console.error('AI playlist generation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate playlist');
    } finally {
      setGeneratingAIPlaylist(false);
    }
  };

  // Guest Request System Functions
  const fetchGuestRequestSettings = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}/guest-request-settings`);
      setGuestRequestSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch guest request settings:', error);
    }
  };

  const fetchGuestRequests = async () => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}/guest-requests`);
      setGuestRequests(response.data);
    } catch (error) {
      console.error('Failed to fetch guest requests:', error);
    }
  };

  const saveGuestRequestSettings = async () => {
    if (guestRequestSettings.enabled && !guestRequestSettings.password.trim()) {
      toast.error('Please set a password for guest access');
      return;
    }
    
    setSavingGuestSettings(true);
    try {
      const response = await axios.put(`${API}/client-portal/${token}/guest-request-settings`, {
        enabled: guestRequestSettings.enabled,
        password: guestRequestSettings.password
      });
      
      setGuestRequestSettings(prev => ({
        ...prev,
        link_code: response.data.link_code,
        guest_link: response.data.guest_link
      }));
      
      toast.success(guestRequestSettings.enabled ? 'Guest requests enabled!' : 'Guest requests disabled');
      setShowGuestRequestSetup(false);
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingGuestSettings(false);
    }
  };

  const approveGuestRequest = async (playlistId, playlistName) => {
    if (!requestToApprove) return;
    
    try {
      await axios.put(
        `${API}/client-portal/${token}/guest-requests/${requestToApprove.id}/approve?playlist_id=${playlistId}&playlist_name=${encodeURIComponent(playlistName)}`
      );
      
      toast.success(`Song added to ${playlistName}!`);
      setShowApproveDialog(false);
      setRequestToApprove(null);
      fetchGuestRequests();
      fetchPortalData(); // Refresh playlists
    } catch (error) {
      toast.error('Failed to approve request');
    }
  };

  const rejectGuestRequest = async (requestId) => {
    try {
      await axios.put(`${API}/client-portal/${token}/guest-requests/${requestId}/reject`);
      toast.success('Request rejected');
      fetchGuestRequests();
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  // Fetch guest data on mount
  useEffect(() => {
    if (token) {
      fetchGuestRequestSettings();
      fetchGuestRequests();
    }
  }, [token]);

  // Send email to vendor
  const handleSendVendorEmail = async () => {
    if (!selectedVendor || !vendorEmailMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingVendorEmail(true);
    try {
      await axios.post(`${API}/client-portal/${token}/email-vendor`, {
        vendor_id: selectedVendor.id,
        message: vendorEmailMessage
      });
      toast.success(`Email sent to ${selectedVendor.name}!`);
      setShowVendorEmailDialog(false);
      setSelectedVendor(null);
      setVendorEmailMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingVendorEmail(false);
    }
  };

  const openVendorEmailDialog = (vendor) => {
    setSelectedVendor(vendor);
    setVendorEmailMessage('');
    setShowVendorEmailDialog(true);
  };

  // Add vendor function
  const handleAddVendor = async () => {
    if (!newVendor.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    if (!newVendor.email.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!newVendor.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    
    setAddingVendor(true);
    try {
      await axios.post(`${API}/client-portal/${token}/vendors`, newVendor);
      toast.success(`Vendor "${newVendor.name}" added successfully!`);
      setShowAddVendorDialog(false);
      setNewVendor({
        name: '',
        category: 'Photographer',
        contact_name: '',
        email: '',
        phone: '',
        notes: ''
      });
      fetchPortalData(); // Refresh to show new vendor
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vendor');
    } finally {
      setAddingVendor(false);
    }
  };

  // Song search
  const searchSongs = async (query, songIndex) => {
    if (!query || query.length < 2) { setSongSearchResults([]); setShowSongSearchResults(false); return; }
    setSearchingSongs(true);
    setSearchingSongIndex(songIndex);
    setShowSongSearchResults(true);
    try {
      // Pass user_id to search DJ's library first
      const userId = data?.user_id || data?.booking?.user_id;
      const params = { query, limit: 25 };
      if (userId) params.user_id = userId;
      const response = await axios.get(`${API}/music/search`, { params });
      setSongSearchResults(response.data.results || []);
    } catch {
      setSongSearchResults([]);
    } finally {
      setSearchingSongs(false);
    }
  };

  const selectSongFromSearch = (result, songIndex) => {
    const updated = [...playlistSongs];
    updated[songIndex] = { 
      title: result.title, 
      artist: result.artist, 
      designation: updated[songIndex].designation || 'must_play' 
    };
    setPlaylistSongs(updated);
    setShowSongSearchResults(false);
    setSongSearchResults([]);
    setSongSearchQuery('');
    setSearchingSongIndex(null);
    // Trigger auto-save if editing existing playlist
    if (editingPlaylist) triggerPlaylistAutoSave();
  };

  // Add Song Modal handlers
  const openAddSongModal = () => {
    setNewSong({ title: '', artist: '', designation: '', comment: '' });
    setModalSearchQuery('');
    setModalSearchResults([]);
    setShowModalSearchResults(false);
    setShowAddSongModal(true);
  };

  // Search songs for the Add Song modal
  const searchSongsForModal = async (query) => {
    setModalSearchQuery(query);
    if (!query || query.length < 2) {
      setModalSearchResults([]);
      setShowModalSearchResults(false);
      return;
    }
    
    setModalSearching(true);
    setShowModalSearchResults(true);
    try {
      // Pass user_id to search DJ's library first
      const userId = data?.user_id || data?.booking?.user_id;
      const params = { query, limit: 25 };
      if (userId) params.user_id = userId;
      const response = await axios.get(`${API}/music/search`, { params });
      setModalSearchResults(response.data.results || []);
    } catch {
      setModalSearchResults([]);
    } finally {
      setModalSearching(false);
    }
  };

  const selectSongFromModalSearch = (result) => {
    setNewSong({
      ...newSong,
      title: result.title,
      artist: result.artist
    });
    setModalSearchQuery('');
    setModalSearchResults([]);
    setShowModalSearchResults(false);
  };

  const handleAddSongFromModal = () => {
    if (!newSong.title.trim()) {
      toast.error('Please enter a song title');
      return;
    }
    if (!newSong.designation) {
      toast.error('Please select a designation (Must Play, Play if Possible, or Do Not Play)');
      return;
    }
    
    // Add the new song to the playlist
    const songToAdd = {
      title: newSong.title.trim(),
      artist: newSong.artist.trim(),
      designation: newSong.designation,
      comment: newSong.comment.trim()
    };
    
    setPlaylistSongs([...playlistSongs, songToAdd]);
    setShowAddSongModal(false);
    setNewSong({ title: '', artist: '', designation: '', comment: '' });
    
    // Trigger auto-save if editing existing playlist
    if (editingPlaylist) triggerPlaylistAutoSave();
    toast.success('Song added!');
  };

  // Contract handlers
  const openSignContractDialog = (contract) => {
    setSelectedContract(contract);
    setSignerName(data?.booking?.client_name || '');
    setShowSignContractDialog(true);
  };

  const handleSignContract = async () => {
    if (!signerName.trim()) { toast.error('Please enter your full name'); return; }
    setSigningContract(true);
    try {
      const response = await axios.put(`${API}/contracts/${selectedContract.id}/sign`, { signer_name: signerName.trim() });
      
      if (response.data.booking_auto_confirmed) {
        toast.success('Contract signed and booking confirmed! 🎉', { duration: 5000 });
      } else {
        toast.success('Contract signed!');
      }
      
      setShowSignContractDialog(false);
      fetchPortalData();
    } catch {
      toast.error('Failed to sign contract');
    } finally {
      setSigningContract(false);
    }
  };

  const openViewContractDialog = (contract) => {
    setSelectedContract(contract);
    setShowViewContractDialog(true);
  };

  const downloadContract = async (contract) => {
    try {
      const response = await axios.get(`${API}/contracts/${contract.id}/download-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `contract-${contract.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download contract');
    }
  };

  // Invoice handlers
  const viewInvoice = async (invoice) => {
    setSelectedInvoice(invoice);
    setLoadingInvoice(true);
    setShowInvoiceDialog(true);
    try {
      const response = await axios.get(`${API}/client-portal/${token}/invoices/${invoice.id}`);
      setInvoiceDetails(response.data);
    } catch {
      toast.error('Failed to load invoice');
    } finally {
      setLoadingInvoice(false);
    }
  };

  const downloadInvoice = async (invoice) => {
    try {
      const response = await axios.get(`${API}/client-portal/${token}/invoices/${invoice.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded!');
    } catch {
      toast.error('Failed to download invoice');
    }
  };

  // Document handlers
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) { toast.error('File must be less than 10MB'); return; }
      setSelectedFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedFile) { toast.error('Please select a file'); return; }
    setUploadingDoc(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        await axios.post(`${API}/client-portal/${token}/documents`, {
          booking_id: data.booking.id,
          filename: `${Date.now()}_${selectedFile.name}`,
          original_filename: selectedFile.name,
          file_type: selectedFile.type,
          file_size: selectedFile.size,
          file_url: e.target.result,
          note: docNote || null
        });
        toast.success('Document uploaded!');
        setShowDocUploadDialog(false);
        setSelectedFile(null);
        setDocNote('');
        fetchPortalData();
      };
      reader.readAsDataURL(selectedFile);
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/client-portal/${token}/documents/${documentId}`);
      toast.success('Document deleted');
      fetchPortalData();
    } catch {
      toast.error('Failed to delete document');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  // Print and Email Timeline handlers
  const handlePrintTimeline = () => {
    const events = sortedTimelineEvents(timeline?.events || []);
    if (events.length === 0) { toast.error('No timeline events to print'); return; }
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Event Timeline - ${booking.event_type}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #333; border-bottom: 2px solid #06b6d4; padding-bottom: 10px; }
            .event-info { color: #666; margin-bottom: 20px; }
            .timeline { margin-top: 30px; }
            .event { display: flex; padding: 15px 0; border-bottom: 1px solid #eee; }
            .time { width: 100px; font-weight: bold; color: #06b6d4; }
            .details { flex: 1; }
            .title { font-weight: 600; color: #333; }
            .description { color: #666; font-size: 14px; margin-top: 5px; }
            .status { font-size: 12px; padding: 2px 8px; border-radius: 10px; margin-left: 10px; }
            .status-pending { background: #fef3c7; color: #d97706; }
            .status-approved { background: #d1fae5; color: #059669; }
            @media print { body { padding: 20px; } @page { margin: 1cm; } }
          </style>
        </head>
        <body>
          <h1>Event Timeline</h1>
          <div class="event-info">
            <p><strong>${booking.client_name}'s ${booking.event_type}</strong></p>
            <p>${formatDate(booking.event_date, true)} at ${booking.venue || 'TBD'}</p>
          </div>
          <div class="timeline">
            ${events.map(event => `
              <div class="event">
                <div class="time">${formatTime(event.time)}</div>
                <div class="details">
                  <span class="title">${event.title}</span>
                  ${event.is_client_request ? `<span class="status status-${event.status || 'pending'}">${event.status || 'Pending'}</span>` : ''}
                  ${event.description ? `<p class="description">${event.description}</p>` : ''}
                </div>
              </div>
            `).join('')}
          </div>
          <p style="margin-top: 30px; color: #999; font-size: 12px;">Printed from ${data?.settings?.company_name || 'Event Portal'}</p>
        </body>
      </html>
    `;
    
    // Use iframe for safer printing (prevents browser crashes)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(printContent);
    printFrame.contentDocument.close();
    
    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => document.body.removeChild(printFrame), 1000);
      }, 250);
    };
  };

  const handleEmailTimeline = async () => {
    const events = sortedTimelineEvents(timeline?.events || []);
    if (events.length === 0) { toast.error('No timeline events to email'); return; }
    
    try {
      await axios.post(`${API}/client-portal/${token}/email-timeline`);
      toast.success('Timeline emailed to you!');
    } catch (error) {
      // Fallback to mailto if API fails
      const eventsList = events.map(e => `${formatTime(e.time)} - ${e.title}${e.description ? ': ' + e.description : ''}`).join('\n');
      const subject = `Event Timeline - ${booking.event_type} (${formatDate(booking.event_date)})`;
      const body = `Event Timeline for ${booking.client_name}'s ${booking.event_type}\nDate: ${formatDate(booking.event_date, true)}\nVenue: ${booking.venue || 'TBD'}\n\n${eventsList}`;
      window.location.href = `mailto:${booking.client_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  // Document print/download/email handlers
  const handleDownloadDocument = (doc) => {
    const link = document.createElement('a');
    link.href = doc.file_url;
    link.download = doc.original_filename;
    link.click();
    toast.success('Download started');
  };

  const handlePrintDocument = (doc) => {
    // For images, use iframe printing
    if (doc.file_type?.startsWith('image/')) {
      const printHtml = `
        <!DOCTYPE html>
        <html>
          <head><title>${doc.original_filename}</title></head>
          <body style="margin:0; display:flex; justify-content:center; align-items:center; min-height:100vh;">
            <img src="${doc.file_url}" style="max-width:100%; max-height:100vh;" />
          </body>
        </html>
      `;
      
      const printFrame = document.createElement('iframe');
      printFrame.style.position = 'fixed';
      printFrame.style.right = '0';
      printFrame.style.bottom = '0';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      document.body.appendChild(printFrame);
      
      printFrame.contentDocument.write(printHtml);
      printFrame.contentDocument.close();
      
      printFrame.onload = () => {
        setTimeout(() => {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          setTimeout(() => document.body.removeChild(printFrame), 1000);
        }, 500);
      };
    } else if (doc.file_type === 'application/pdf') {
      // For PDFs, open in new tab (browser handles PDF printing)
      window.open(doc.file_url, '_blank');
      toast.info('PDF opened - use browser print function (Ctrl/Cmd + P)');
    } else {
      // For other file types, just open
      window.open(doc.file_url, '_blank');
      toast.info('Document opened - use browser print function');
    }
  };

  const handleEmailDocumentsList = async () => {
    if (!documents || documents.length === 0) { toast.error('No documents to email'); return; }
    
    try {
      await axios.post(`${API}/client-portal/${token}/email-documents-list`);
      toast.success('Documents list emailed to you!');
    } catch (error) {
      // Fallback to mailto
      const docsList = documents.map(d => `- ${d.original_filename} (${formatFileSize(d.file_size)})`).join('\n');
      const subject = `Documents List - ${booking.event_type} (${formatDate(booking.event_date)})`;
      const body = `Documents for ${booking.client_name}'s ${booking.event_type}\n\n${docsList}\n\nAccess your documents at: ${window.location.href.replace(/\/[^\/]*$/, '')}`;
      window.location.href = `mailto:${booking.client_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };

  // Forms print/email handlers
  const handlePrintForm = (form, response) => {
    if (!response) { toast.error('Form not completed yet'); return; }
    
    const formatFieldResponse = (field, responses) => {
      const value = responses[field.name];
      if (!value && value !== false) return '<em style="color: #999;">Not answered</em>';
      
      if (field.type === 'repeater' || field.type === 'repeater3') {
        if (!Array.isArray(value) || value.length === 0) return '<em style="color: #999;">No entries</em>';
        return '<ul style="margin: 5px 0; padding-left: 20px;">' + 
          value.map(row => `<li>${Object.values(row).filter(Boolean).join(' - ')}</li>`).join('') + 
          '</ul>';
      }
      if (field.type === 'columns2' || field.type === 'columns3') {
        return Object.values(value).filter(Boolean).join(' | ') || '<em style="color: #999;">Not answered</em>';
      }
      if (field.type === 'checkbox') {
        return value === true || value === 'true' ? '✓ Yes' : '✗ No';
      }
      if (field.type === 'checkbox_conditional') {
        if (!value?.checked) return '✗ No';
        const conditionalLabel = field.conditionalFieldLabel || 'Details';
        const conditionalValue = value.conditionalValue || '<em style="color: #999;">Not specified</em>';
        return `✓ Yes<br/><span style="margin-left: 15px; color: #666;">${conditionalLabel}: ${conditionalValue}</span>`;
      }
      return String(value);
    };
    
    const fieldsHtml = form.fields?.map(field => {
      if (field.type === 'section') {
        return `<h3 style="color: #06b6d4; margin-top: 20px; border-bottom: 1px solid #eee; padding-bottom: 5px;">${field.label}</h3>`;
      }
      if (field.type === 'comment') {
        return `<p style="color: #666; font-style: italic; background: #f5f5f5; padding: 10px; border-radius: 5px;">${field.label}</p>`;
      }
      return `
        <div style="margin-bottom: 15px;">
          <strong style="color: #333;">${field.label}</strong>
          <div style="margin-top: 5px; color: #444;">${formatFieldResponse(field, response.responses || {})}</div>
        </div>
      `;
    }).join('') || '';
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${form.name} - ${booking.client_name}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #333; border-bottom: 2px solid #06b6d4; padding-bottom: 10px; }
            .event-info { color: #666; margin-bottom: 30px; }
            @media print { body { padding: 20px; } @page { margin: 1cm; } }
          </style>
        </head>
        <body>
          <h1>${form.name}</h1>
          <div class="event-info">
            <p><strong>${booking.client_name}'s ${booking.event_type}</strong></p>
            <p>${formatDate(booking.event_date, true)}</p>
          </div>
          ${fieldsHtml}
          <p style="margin-top: 30px; color: #999; font-size: 12px;">Printed from ${data?.settings?.company_name || 'Event Portal'}</p>
        </body>
      </html>
    `;
    
    // Use iframe for safer printing (prevents browser crashes)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(printContent);
    printFrame.contentDocument.close();
    
    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => document.body.removeChild(printFrame), 1000);
      }, 250);
    };
  };

  const handlePrintAllForms = () => {
    if (!data.form_responses || data.form_responses.length === 0) {
      toast.error('No completed forms to print');
      return;
    }
    
    const formsHtml = planning_forms?.filter(form => {
      const response = data.form_responses?.find(r => r.form_id === form.id);
      return !!response;
    }).map(form => {
      const response = data.form_responses?.find(r => r.form_id === form.id);
      
      const formatFieldResponse = (field, responses) => {
        const value = responses[field.name];
        if (!value && value !== false) return '<em style="color: #999;">Not answered</em>';
        if (field.type === 'repeater' || field.type === 'repeater3') {
          if (!Array.isArray(value) || value.length === 0) return '<em style="color: #999;">No entries</em>';
          return '<ul style="margin: 5px 0; padding-left: 20px;">' + 
            value.map(row => `<li>${Object.values(row).filter(Boolean).join(' - ')}</li>`).join('') + '</ul>';
        }
        if (field.type === 'columns2' || field.type === 'columns3') {
          return Object.values(value).filter(Boolean).join(' | ') || '<em style="color: #999;">Not answered</em>';
        }
        if (field.type === 'checkbox') {
          return value === true || value === 'true' ? '✓ Yes' : '✗ No';
        }
        if (field.type === 'checkbox_conditional') {
          if (!value?.checked) return '✗ No';
          const conditionalLabel = field.conditionalFieldLabel || 'Details';
          const conditionalValue = value.conditionalValue || '<em style="color: #999;">Not specified</em>';
          return `✓ Yes<br/><span style="margin-left: 15px; color: #666;">${conditionalLabel}: ${conditionalValue}</span>`;
        }
        return String(value);
      };
      
      const fieldsHtml = form.fields?.map(field => {
        if (field.type === 'section') return `<h4 style="color: #06b6d4; margin-top: 15px;">${field.label}</h4>`;
        if (field.type === 'comment') return `<p style="color: #666; font-style: italic;">${field.label}</p>`;
        return `<div style="margin-bottom: 10px;"><strong>${field.label}:</strong> ${formatFieldResponse(field, response?.responses || {})}</div>`;
      }).join('') || '';
      
      return `<div style="margin-bottom: 40px; page-break-inside: avoid;"><h2 style="color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px;">${form.name}</h2>${fieldsHtml}</div>`;
    }).join('') || '';
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>All Forms - ${booking.client_name}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
            h1 { color: #333; border-bottom: 2px solid #06b6d4; padding-bottom: 10px; }
            @media print { body { padding: 20px; } @page { margin: 1cm; } }
          </style>
        </head>
        <body>
          <h1>Planning Forms</h1>
          <p style="color: #666; margin-bottom: 30px;"><strong>${booking.client_name}'s ${booking.event_type}</strong> - ${formatDate(booking.event_date, true)}</p>
          ${formsHtml}
          <p style="margin-top: 30px; color: #999; font-size: 12px;">Printed from ${data?.settings?.company_name || 'Event Portal'}</p>
        </body>
      </html>
    `;
    
    // Use iframe for safer printing (prevents browser crashes)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(printContent);
    printFrame.contentDocument.close();
    
    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => document.body.removeChild(printFrame), 1000);
      }, 250);
    };
  };

  const handleEmailForms = async () => {
    if (!data.form_responses || data.form_responses.length === 0) {
      toast.error('No completed forms to email');
      return;
    }
    
    try {
      await axios.post(`${API}/client-portal/${token}/email-forms`);
      toast.success('Forms emailed to you!');
    } catch (error) {
      toast.error('Failed to send email. Please try printing instead.');
    }
  };

  // Playlist print/email handlers
  const handlePrintPlaylist = (playlist) => {
    if (!playlist.songs || playlist.songs.length === 0) {
      toast.error('Playlist is empty');
      return;
    }
    
    // Helper to get designation display
    const getDesignationBadge = (designation) => {
      switch (designation) {
        case 'must_play':
          return '<span style="background: #22c55e20; color: #22c55e; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;">Must Play</span>';
        case 'play_if_possible':
          return '<span style="background: #f59e0b20; color: #f59e0b; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;">Play if Possible</span>';
        case 'do_not_play':
          return '<span style="background: #ef444420; color: #ef4444; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;">Do Not Play</span>';
        default:
          return '';
      }
    };
    
    const songsHtml = playlist.songs.map((song, idx) => `
      <tr>
        <td style="padding: 10px 8px; border-bottom: 1px solid #eee; color: #999; width: 40px; vertical-align: top;">${idx + 1}</td>
        <td style="padding: 10px 8px; border-bottom: 1px solid #eee; vertical-align: top;">
          <div><strong>${song.title || 'Unknown'}</strong></div>
          <div style="color: #666; font-size: 13px;">${song.artist || 'Unknown Artist'}</div>
          ${song.comment ? `<div style="color: #888; font-size: 12px; font-style: italic; margin-top: 4px;">"${song.comment}"</div>` : ''}
        </td>
        <td style="padding: 10px 8px; border-bottom: 1px solid #eee; width: 120px; text-align: right; vertical-align: top;">
          ${getDesignationBadge(song.designation)}
        </td>
      </tr>
    `).join('');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${playlist.name} - ${booking.client_name}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            h1 { color: #333; border-bottom: 2px solid #8b5cf6; padding-bottom: 10px; margin-bottom: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            @media print { body { padding: 20px; } @page { margin: 1cm; } }
          </style>
        </head>
        <body>
          <h1>${playlist.name}</h1>
          <p style="color: #666; margin: 5px 0;"><strong>${booking.client_name}'s ${booking.event_type}</strong> - ${formatDate(booking.event_date, true)}</p>
          <p style="color: #666; margin: 5px 0;">${playlist.songs.length} songs</p>
          <table>${songsHtml}</table>
          <p style="margin-top: 30px; color: #999; font-size: 12px;">Printed from ${data?.settings?.company_name || 'Event Portal'}</p>
        </body>
      </html>
    `;
    
    // Use iframe for safer printing (prevents browser crashes)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(printContent);
    printFrame.contentDocument.close();
    
    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => document.body.removeChild(printFrame), 1000);
      }, 250);
    };
  };

  const handlePrintAllPlaylists = () => {
    if (!playlists || playlists.length === 0) {
      toast.error('No playlists to print');
      return;
    }
    
    // Helper to get designation display
    const getDesignationBadge = (designation) => {
      switch (designation) {
        case 'must_play':
          return '<span style="background: #22c55e20; color: #22c55e; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 500;">Must Play</span>';
        case 'play_if_possible':
          return '<span style="background: #f59e0b20; color: #f59e0b; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 500;">Play if Possible</span>';
        case 'do_not_play':
          return '<span style="background: #ef444420; color: #ef4444; padding: 2px 6px; border-radius: 4px; font-size: 10px; font-weight: 500;">Do Not Play</span>';
        default:
          return '';
      }
    };
    
    const playlistsHtml = playlists.map(playlist => {
      const songsHtml = (playlist.songs || []).map((song, idx) => `
        <tr>
          <td style="padding: 8px 6px; border-bottom: 1px solid #eee; color: #999; width: 30px; vertical-align: top;">${idx + 1}</td>
          <td style="padding: 8px 6px; border-bottom: 1px solid #eee; vertical-align: top;">
            <div><strong>${song.title || 'Unknown'}</strong></div>
            <div style="color: #666; font-size: 12px;">${song.artist || 'Unknown Artist'}</div>
            ${song.comment ? `<div style="color: #888; font-size: 11px; font-style: italic; margin-top: 3px;">"${song.comment}"</div>` : ''}
          </td>
          <td style="padding: 8px 6px; border-bottom: 1px solid #eee; width: 100px; text-align: right; vertical-align: top;">
            ${getDesignationBadge(song.designation)}
          </td>
        </tr>
      `).join('');
      
      return `
        <div style="margin-bottom: 40px; page-break-inside: avoid;">
          <h2 style="color: #333; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-bottom: 5px;">${playlist.name}</h2>
          <p style="color: #666; font-size: 13px; margin: 5px 0;">${playlist.songs?.length || 0} songs</p>
          <table style="width: 100%; border-collapse: collapse;">${songsHtml}</table>
        </div>
      `;
    }).join('');
    
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>All Playlists - ${booking.client_name}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            h1 { color: #333; border-bottom: 2px solid #8b5cf6; padding-bottom: 10px; }
            @media print { body { padding: 20px; } @page { margin: 1cm; } }
          </style>
        </head>
        <body>
          <h1>Music Playlists</h1>
          <p style="color: #666; margin-bottom: 30px;"><strong>${booking.client_name}'s ${booking.event_type}</strong> - ${formatDate(booking.event_date, true)}</p>
          ${playlistsHtml}
          <p style="margin-top: 30px; color: #999; font-size: 12px;">Printed from ${data?.settings?.company_name || 'Event Portal'}</p>
        </body>
      </html>
    `;
    
    // Use iframe for safer printing (prevents browser crashes)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(printContent);
    printFrame.contentDocument.close();
    
    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => document.body.removeChild(printFrame), 1000);
      }, 250);
    };
  };

  const handleEmailPlaylists = async () => {
    if (!playlists || playlists.length === 0) {
      toast.error('No playlists to email');
      return;
    }
    
    try {
      await axios.post(`${API}/client-portal/${token}/email-playlists`);
      toast.success('Playlists emailed to you!');
    } catch (error) {
      toast.error('Failed to send email. Please try printing instead.');
    }
  };

  // Email handlers
  const openEmailDialog = (replyTo = null) => {
    if (replyTo) {
      setReplyingTo(replyTo);
      setEmailForm({
        subject: `Re: ${replyTo.subject.replace(/^Re: /i, '').replace(/^\[Client Portal\] /i, '')}`,
        body: ''
      });
    } else {
      setReplyingTo(null);
      setEmailForm({ subject: '', body: '' });
    }
    setShowComposeEmailDialog(true);
  };

  const sendClientEmail = async () => {
    if (!emailForm.subject.trim() || !emailForm.body.trim()) { toast.error('Please enter subject and message'); return; }
    setSendingEmail(true);
    try {
      await axios.post(`${API}/client-portal/${token}/send-email`, {
        subject: emailForm.subject, body: emailForm.body, reply_to_id: replyingTo?.id
      });
      toast.success('Email sent!');
      setShowComposeEmailDialog(false);
      setEmailForm({ subject: '', body: '' });
      setReplyingTo(null);
      fetchCommunications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const toggleEmailExpand = (emailId) => {
    setExpandedEmails(prev => ({ ...prev, [emailId]: !prev[emailId] }));
  };

  // Add-on handlers
  const handleRequestAddon = async (addon) => {
    setSubmittingAddonRequest(true);
    try {
      await axios.post(`${API}/client-portal/${token}/addon-requests`, {
        addon_id: addon.id, client_notes: addonRequestNotes
      });
      toast.success('Add-on request submitted!');
      setRequestingAddon(null);
      setAddonRequestNotes('');
      fetchAvailableAddons();
      fetchAddonRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmittingAddonRequest(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full animate-pulse" style={{ backgroundColor: djPrimaryColor + '40' }} />
          <p className="text-white/60 text-lg">Loading your portal...</p>
        </div>
      </div>
    );
  }

  if (!data || !data.booking) {
    if (data?.maintenance_mode) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
          <div className="text-center bg-white/10 backdrop-blur-xl rounded-3xl p-12 border border-white/20 max-w-lg">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
              <svg className="w-8 h-8 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
            </div>
            <h1 className="text-2xl font-bold mb-4 text-white">Down for Maintenance</h1>
            <p className="text-white/70 text-lg leading-relaxed">{data.maintenance_message}</p>
            <p className="text-white/40 text-sm mt-6">Please check back shortly.</p>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
        <div className="text-center bg-white/10 backdrop-blur-xl rounded-3xl p-12 border border-white/20">
          <h1 className="text-3xl font-bold mb-4 text-white">Portal Not Found</h1>
          <p className="text-white/60">This link is invalid or expired.</p>
        </div>
      </div>
    );
  }

  const { booking, contract, contracts, invoices, payments, playlists, timeline, planning_forms, messages, documents, tasks, vendors } = data;
  const countdown = getCountdown();
  const balance = (booking.total_amount || 0) - (booking.paid_amount || 0);
  const pendingTasks = tasks?.filter(t => t.status === 'pending') || [];
  const pendingVenueChange = venueChangeRequests.find(r => r.status === 'pending');

  // Navigation items
  const navItems = [
    { id: 'dashboard', label: 'Home', icon: Home },
    { id: 'seating', label: 'Seating Chart', icon: LayoutGrid },
    { id: 'tasks', label: 'Tasks', icon: Check, count: pendingTasks?.length },
    { id: 'contracts', label: 'Contracts', icon: FileText, count: contracts?.length },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'messages', label: 'Messages', icon: MessageSquare },
    { id: 'documents', label: 'Documents', icon: File },
    { id: 'vendors', label: 'Vendors', icon: Building2, count: vendors?.length },
    { id: 'addons', label: 'Add-ons', icon: Plus },
    { id: 'experience', label: 'Guest Experience', icon: PartyPopper },
    { id: 'settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="client-portal-v2">
      {/* Glass Header */}
      <header className="sticky top-0 z-50 bg-black/20 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {data?.settings?.logo && !logoError && (
              <img 
                src={data.settings.logo} 
                alt="Logo" 
                className="w-10 h-10 rounded-xl object-contain"
                onError={() => setLogoError(true)}
                data-testid="portal-logo"
              />
            )}
            <div>
              <h1 className="text-white font-semibold text-lg">{data?.settings?.company_name || 'Event Portal'}</h1>
              <p className="text-white/50 text-xs">{booking.client_name}'s {booking.event_type}</p>
            </div>
          </div>
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
            data-testid="mobile-menu-toggle"
          >
            <Menu className="w-6 h-6 text-white" />
          </button>
          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map(item => (
              <button
                key={item.id}
                onClick={() => setActiveSection(item.id)}
                className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                  activeSection === item.id
                    ? 'text-white bg-white/20'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
                data-testid={`nav-${item.id}`}
              >
                {item.label}
              </button>
            ))}
            {/* Desktop Event Switcher */}
            {allBookings.length > 1 && (
              <div className="relative ml-4 group">
                <button className="px-3 py-2 rounded-xl text-sm font-medium text-white/60 hover:text-white hover:bg-white/10 transition-all flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Switch Event
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className="absolute right-0 top-full mt-2 w-64 bg-slate-800 border border-white/20 rounded-xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                  <div className="p-2">
                    <p className="text-white/40 text-xs uppercase tracking-wider px-2 py-2">Your Events</p>
                    {allBookings.map(b => (
                      <button
                        key={b.id}
                        onClick={() => switchToBooking(b.client_portal_token)}
                        className={`w-full text-left p-3 rounded-lg transition-all ${
                          b.id === currentBookingId ? 'bg-white/20' : 'hover:bg-white/10'
                        }`}
                        data-testid={`switch-booking-${b.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <p className="text-white text-sm font-medium">{b.event_type}</p>
                          {getBookingDisplayStatus(b) && (
                            <span className={`px-2 py-0.5 text-xs rounded-full ${getBookingDisplayStatus(b).color}`}>
                              {getBookingDisplayStatus(b).label}
                            </span>
                          )}
                        </div>
                        <p className="text-white/50 text-xs">{formatDate(b.event_date)}</p>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </nav>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
          <div 
            className="absolute right-0 top-0 h-full w-72 bg-slate-900 border-l border-white/10 p-4 animate-in slide-in-from-right duration-300"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <span className="text-white font-semibold">Menu</span>
              <button onClick={() => setMobileMenuOpen(false)} className="p-2 rounded-xl hover:bg-white/10">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
            <nav className="space-y-1">
              {navItems.map(item => (
                <button
                  key={item.id}
                  onClick={() => { setActiveSection(item.id); setMobileMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${
                    activeSection === item.id
                      ? 'text-white bg-white/20'
                      : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span className="flex-1">{item.label}</span>
                  {item.count > 0 && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-white/20">{item.count}</span>
                  )}
                </button>
              ))}
            </nav>
            {/* Event Switcher */}
            {allBookings.length > 1 && (
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Your Events</p>
                <div className="space-y-2">
                  {allBookings.map(b => (
                    <button
                      key={b.id}
                      onClick={() => switchToBooking(b.client_portal_token)}
                      className={`w-full text-left p-3 rounded-xl transition-all ${
                        b.id === currentBookingId ? 'bg-white/20 border border-white/30' : 'bg-white/5 hover:bg-white/10'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-white text-sm font-medium">{b.event_type}</p>
                        {getBookingDisplayStatus(b) && (
                          <span className={`px-2 py-0.5 text-xs rounded-full ${getBookingDisplayStatus(b).color}`}>
                            {getBookingDisplayStatus(b).label}
                          </span>
                        )}
                      </div>
                      <p className="text-white/50 text-xs">{formatDate(b.event_date)}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Dashboard Section */}
        {activeSection === 'dashboard' && (
          <div className="space-y-6">
            {/* Hero Card */}
            <div 
              className="relative overflow-hidden rounded-3xl p-8 md:p-12"
              style={{ background: `linear-gradient(135deg, ${djPrimaryColor}40, ${djSecondaryColor}40)` }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-black/20 to-transparent" />
              <div className="relative z-10">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                  {booking.client_name}'s {booking.event_type}
                </h2>
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <p className="text-white/70 text-lg">{formatDate(booking.event_date, true)}</p>
                  {getBookingDisplayStatus(booking) && (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getBookingDisplayStatus(booking).color}`}>
                      {getBookingDisplayStatus(booking).label}
                    </span>
                  )}
                </div>
                
                {/* Outstanding Requirements for Pending Bookings */}
                {booking.status?.toLowerCase() === 'pending' && (
                  <div className="mb-6">
                    {(() => {
                      const requirements = getOutstandingRequirements(booking, data?.contracts);
                      if (requirements.length === 0) return null;
                      
                      return (
                        <div className="bg-amber-500/20 backdrop-blur-sm rounded-xl p-4 border border-amber-500/30">
                          <p className="text-amber-300 text-sm font-medium mb-2 flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            Complete these steps to confirm your booking:
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {requirements.map((req, idx) => (
                              <button
                                key={idx}
                                onClick={() => setActiveSection(req.type === 'contract' ? 'contracts' : 'payments')}
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/30 hover:bg-amber-500/40 rounded-lg text-white text-sm transition-all"
                              >
                                {req.type === 'contract' ? <FileText className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
                                {req.label}
                                <ArrowRight className="w-3 h-3" />
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
                
                {countdown !== null && countdown > 0 && booking.status?.toLowerCase() !== 'pending' && (
                  <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-sm rounded-2xl px-6 py-3">
                    <Calendar className="w-6 h-6 text-white" />
                    <div>
                      <p className="text-white text-2xl font-bold">{countdown}</p>
                      <p className="text-white/70 text-sm">days to go</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <QuickStat
                icon={DollarSign}
                label="Balance Due"
                value={`$${balance.toFixed(2)}`}
                color={balance > 0 ? '#f59e0b' : '#22c55e'}
                onClick={() => setActiveSection('payments')}
              />
              <QuickStat
                icon={ClipboardList}
                label="Forms"
                value={`${data.form_responses?.length || 0}/${planning_forms?.length || 0}`}
                color={djPrimaryColor}
                onClick={() => setActiveSection('forms')}
              />
              <QuickStat
                icon={Music}
                label="Playlists"
                value={playlists?.length || 0}
                color={djSecondaryColor}
                onClick={() => setActiveSection('music')}
              />
              <QuickStat
                icon={Clock}
                label="Timeline"
                value={timeline?.events?.length || 0}
                color="#8b5cf6"
                onClick={() => setActiveSection('timeline')}
              />
            </div>

            {/* Bento Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Venue Card */}
              <BentoCard
                title="Venue"
                icon={MapPin}
                color={djPrimaryColor}
                onClick={() => setActiveSection('venue')}
              >
                <div className="space-y-2">
                  <p className="text-white font-medium">{venueInfo?.name || booking.venue}</p>
                  {venueInfo?.address && (
                    <p className="text-white/50 text-sm">{venueInfo.address}</p>
                  )}
                </div>
              </BentoCard>

              {/* Tasks Card */}
              <BentoCard
                title="Tasks"
                icon={Check}
                color="#f59e0b"
                onClick={() => setActiveSection('tasks')}
                data-testid="dashboard-tasks-card"
              >
                <p className="text-white/50 text-sm">
                  {tasks?.filter(t => t.status === 'completed').length || 0}/{tasks?.length || 0} completed
                </p>
              </BentoCard>

              {/* Timeline Card */}
              <BentoCard
                title="Timeline"
                icon={Clock}
                color={djSecondaryColor}
                onClick={() => setActiveSection('timeline')}
              >
                <p className="text-white/50 text-sm">
                  {timeline?.events?.length || 0} events scheduled
                </p>
              </BentoCard>

              {/* Package Card */}
              {(data?.package_info || data?.addon_details?.length > 0) && (
                <BentoCard
                  title="Your Package"
                  icon={FileText}
                  color="#8b5cf6"
                >
                  <div className="space-y-2">
                    {data?.package_info?.name && (
                      <p className="text-white font-medium">{data.package_info.name}</p>
                    )}
                    {data?.package_info?.description && (
                      <p className="text-white/50 text-xs">{data.package_info.description}</p>
                    )}
                    {data?.addon_details?.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-white/10">
                        <p className="text-white/70 text-xs mb-1">Add-ons included:</p>
                        <div className="flex flex-wrap gap-1">
                          {data.addon_details.map((addon) => (
                            <span 
                              key={addon.id} 
                              className="text-xs bg-white/10 text-white/80 px-2 py-0.5 rounded-full"
                            >
                              {addon.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </BentoCard>
              )}

              {/* Contract Card */}
              <BentoCard
                title="Contracts"
                icon={FileText}
                color="#8b5cf6"
                onClick={() => contracts?.length > 0 && setActiveSection('contracts')}
              >
                {contracts?.length > 0 ? (
                  <div className="space-y-1">
                    {contracts.length === 1 ? (
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${
                          contracts[0].status === 'fully_signed' ? 'bg-green-400' :
                          contracts[0].client_signed_at ? 'bg-amber-400' : 'bg-white/40'
                        }`} />
                        <span className="text-white/70 text-sm">
                          {contracts[0].status === 'fully_signed' ? 'Fully Signed' :
                           contracts[0].client_signed_at ? 'Awaiting Counter-Sign' : 'Needs Signature'}
                        </span>
                      </div>
                    ) : (
                      <>
                        <p className="text-white/70 text-sm">{contracts.length} contracts</p>
                        <div className="flex gap-1 mt-1">
                          {contracts.map((c, i) => (
                            <span key={i} className={`w-2 h-2 rounded-full ${
                              c.status === 'fully_signed' ? 'bg-green-400' :
                              c.client_signed_at ? 'bg-amber-400' : 'bg-white/40'
                            }`} title={c.status === 'fully_signed' ? 'Fully Signed' : c.client_signed_at ? 'Awaiting Counter-Sign' : 'Needs Signature'}></span>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-white/50 text-sm">No contract yet</p>
                )}
              </BentoCard>

              {/* Messages Card */}
              <BentoCard
                title="Messages"
                icon={MessageSquare}
                color="#06b6d4"
                onClick={() => setActiveSection('messages')}
              >
                <p className="text-white/50 text-sm">
                  {messages?.length || 0} messages
                </p>
              </BentoCard>

              {/* Payments Card */}
              <BentoCard
                title="Payments"
                icon={CreditCard}
                color="#22c55e"
                onClick={() => setActiveSection('payments')}
              >
                <div className="space-y-1">
                  <p className="text-white font-medium">${(booking.paid_amount || 0).toFixed(2)} paid</p>
                  <p className="text-white/50 text-sm">of ${(booking.total_amount || 0).toFixed(2)}</p>
                </div>
              </BentoCard>

              {/* Guest Experience Card */}
              <BentoCard
                title="Guest Experience"
                icon={PartyPopper}
                color="#e91e63"
                onClick={() => setActiveSection('experience')}
                data-testid="dashboard-experience-card"
              >
                <div className="space-y-2">
                  <p className="text-white/50 text-sm">Live song requests, photo sharing & more for your guests</p>
                  <p className="text-xs font-medium" style={{ color: '#e91e63' }}>Set up & customize &rarr;</p>
                </div>
              </BentoCard>
            </div>
          </div>
        )}

        {/* Venue Section */}
        {activeSection === 'venue' && (
          <div className="space-y-6">
            <SectionHeader title="Venue Details" icon={MapPin} color={djPrimaryColor} />
            
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden">
              {/* Venue Image */}
              <div className="relative h-48 md:h-64 bg-gradient-to-br from-white/10 to-white/5">
                {venueInfo?.image ? (
                  <img 
                    src={venueInfo.image} 
                    alt={venueInfo.name}
                    className="w-full h-full object-cover"
                    data-testid="venue-image"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center">
                    <Building2 className="w-16 h-16 text-white/20 mb-4" />
                    <p className="text-white/40 text-sm mb-3">No venue image available</p>
                    <Button
                      onClick={() => setShowVenueImageUpload(true)}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="upload-venue-image-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Add Photo
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Venue Info */}
              <div className="p-6 space-y-6">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">{venueInfo?.name || booking.venue}</h3>
                  {venueInfo?.address && (
                    <p className="text-white/60 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {[venueInfo.address, venueInfo.city, venueInfo.state, venueInfo.zip_code].filter(Boolean).join(', ')}
                    </p>
                  )}
                </div>
                
                {(venueInfo?.contact_name || venueInfo?.contact_phone || venueInfo?.parking_info) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {venueInfo?.contact_name && (
                      <InfoItem label="Contact" value={venueInfo.contact_name} />
                    )}
                    {venueInfo?.contact_phone && (
                      <InfoItem label="Phone" value={venueInfo.contact_phone} />
                    )}
                    {venueInfo?.parking_info && (
                      <InfoItem label="Parking" value={venueInfo.parking_info} className="md:col-span-2" />
                    )}
                  </div>
                )}
                
                {/* Venue Change Request */}
                <div className="pt-6 border-t border-white/10">
                  {pendingVenueChange ? (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Clock className="w-5 h-5 text-amber-400" />
                        <span className="text-amber-400 font-medium">Venue Change Pending</span>
                      </div>
                      <p className="text-white/70 text-sm">
                        Requested change to: <span className="text-white">{pendingVenueChange.new_venue_name}</span>
                      </p>
                    </div>
                  ) : (
                    <Button
                      onClick={() => setShowVenueChangeDialog(true)}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="request-venue-change-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Request Venue Change
                    </Button>
                  )}
                </div>
                
                {/* Change Request History */}
                {venueChangeRequests.length > 0 && (
                  <div className="pt-6 border-t border-white/10">
                    <h4 className="text-white/60 text-sm uppercase tracking-wider mb-4">Change Request History</h4>
                    <div className="space-y-3">
                      {venueChangeRequests.map(req => (
                        <div 
                          key={req.id}
                          className={`p-4 rounded-xl ${
                            req.status === 'approved' ? 'bg-green-500/10 border border-green-500/30' :
                            req.status === 'declined' ? 'bg-red-500/10 border border-red-500/30' :
                            'bg-amber-500/10 border border-amber-500/30'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-white font-medium">{req.new_venue_name}</span>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              req.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                              req.status === 'declined' ? 'bg-red-500/20 text-red-400' :
                              'bg-amber-500/20 text-amber-400'
                            }`}>
                              {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                            </span>
                          </div>
                          {req.dj_notes && (
                            <p className="text-white/50 text-sm">{req.dj_notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Timeline Section */}
        {activeSection === 'timeline' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <SectionHeader title="Event Timeline" icon={Clock} color={djSecondaryColor} />
              <div className="flex items-center gap-2">
                {timeline?.events && timeline.events.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handlePrintTimeline()}
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="print-timeline-btn"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Print
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleEmailTimeline()}
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="email-timeline-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email to Me
                    </Button>
                  </>
                )}
                <Button
                  onClick={openTimelineDialog}
                  className="text-white"
                  style={{ backgroundColor: editLockout.timeline_locked ? '#4a5568' : djPrimaryColor }}
                  data-testid="add-timeline-event-btn"
                  disabled={editLockout.timeline_locked}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {editLockout.timeline_locked ? 'Editing Locked' : 'Add Event'}
                </Button>
              </div>
            </div>
            
            {/* Lockout warning or last edit date note */}
            {editLockout.timeline_locked ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                <p className="text-amber-400 text-sm flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  {editLockout.message}
                </p>
              </div>
            ) : editLockout.last_edit_date && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                <p className="text-blue-400 text-sm flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  Last day to make changes: <span className="font-semibold">{editLockout.last_edit_date}</span>
                </p>
              </div>
            )}
            
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6" id="timeline-content">
              {timeline?.events && timeline.events.length > 0 ? (
                <div className="space-y-4">
                  {sortedTimelineEvents(timeline.events).map((event, idx) => (
                    <div 
                      key={event.id || idx}
                      className={`flex gap-4 p-4 rounded-2xl ${
                        event.is_client_request ? 'bg-white/10 border border-white/20' : 'bg-white/5'
                      }`}
                      data-testid={`timeline-event-${idx}`}
                    >
                      {editingTimelineEvent === event.id ? (
                        /* Editing mode */
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-white/70 text-sm">Editing Event</span>
                            <div className="flex items-center gap-2 text-sm">
                              {timelineAutoSaveStatus === 'saving' && (
                                <span className="text-amber-400 flex items-center gap-1">
                                  <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                                  Saving...
                                </span>
                              )}
                              {timelineAutoSaveStatus === 'saved' && (
                                <span className="text-green-400 flex items-center gap-1">
                                  <Check className="w-4 h-4" />
                                  Saved
                                </span>
                              )}
                              {timelineAutoSaveStatus === '' && (
                                <span className="text-white/40 text-xs">Auto-saves</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Input
                              value={editingTimelineData.time}
                              onChange={(e) => {
                                setEditingTimelineData({ ...editingTimelineData, time: e.target.value });
                                triggerTimelineAutoSave();
                              }}
                              placeholder="Time (e.g., 6:00 PM)"
                              className="w-32 bg-white/10 border-white/20 text-white"
                            />
                            <Input
                              value={editingTimelineData.title}
                              onChange={(e) => {
                                setEditingTimelineData({ ...editingTimelineData, title: e.target.value });
                                triggerTimelineAutoSave();
                              }}
                              placeholder="Event title"
                              className="flex-1 bg-white/10 border-white/20 text-white"
                            />
                          </div>
                          <Input
                            value={editingTimelineData.description}
                            onChange={(e) => {
                              setEditingTimelineData({ ...editingTimelineData, description: e.target.value });
                              triggerTimelineAutoSave();
                            }}
                            placeholder="Description (optional)"
                            className="bg-white/10 border-white/20 text-white"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEditingTimeline}
                              className="border-white/20 text-white"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={saveTimelineEventEdit}
                              style={{ backgroundColor: djPrimaryColor }}
                              className="text-white"
                            >
                              Save & Close
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* Display mode */
                        <>
                          <div className="w-20 text-center">
                            <p className="text-lg font-bold" style={{ color: djPrimaryColor }}>{formatTime(event.time)}</p>
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{event.title}</span>
                              {event.is_client_request && (
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  event.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                                  event.status === 'declined' ? 'bg-red-500/20 text-red-400' :
                                  'bg-amber-500/20 text-amber-400'
                                }`}>
                                  {event.status || 'Pending'}
                                </span>
                              )}
                            </div>
                            {event.description && (
                              <p className="text-white/50 text-sm mt-1">{event.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {!editLockout.timeline_locked && (
                              <>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => startEditingTimelineEvent(event)}
                                  className="text-white/60 hover:text-white hover:bg-white/10"
                                  title="Edit event"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => deleteTimelineEvent(event.id)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                  title="Remove event"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={Clock}
                  title="No Timeline Events"
                  description="Add events to build your schedule"
                  action={{ label: 'Add Event', onClick: openTimelineDialog }}
                  color={djPrimaryColor}
                />
              )}
            </div>
          </div>
        )}

        {/* Seating Chart Section */}
        {activeSection === 'seating' && (
          <div className="space-y-6" data-testid="seating-section">
            <SectionHeader title="Seating Chart" icon={LayoutGrid} color="#8b5cf6" />
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden" style={{ height: 'calc(100vh - 250px)', minHeight: '600px' }}>
              <SeatingChartEditor 
                token={token}
                isClientPortal={true}
              />
            </div>
          </div>
        )}

        {/* Tasks Section */}
        {activeSection === 'tasks' && (
          <div className="space-y-6" data-testid="tasks-section">
            <SectionHeader title="Your Tasks" icon={Check} color="#f59e0b" />
            
            {tasks && tasks.length > 0 ? (
              <div className="space-y-4">
                {/* Pending Tasks */}
                {pendingTasks.length > 0 && (
                  <div className="space-y-3">
                    <h3 className="text-white/70 text-sm font-medium uppercase tracking-wide">Pending</h3>
                    {pendingTasks.map((task) => (
                      <div
                        key={task.id}
                        className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-4 hover:border-white/20 transition-colors"
                        data-testid={`task-item-${task.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                            <Clock className="w-5 h-5 text-amber-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-medium">{task.title}</h4>
                            {task.description && (
                              <p className="text-white/50 text-sm mt-1">{task.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2">
                              {task.due_date && (
                                <span className={`text-xs px-2 py-1 rounded-lg ${
                                  new Date(task.due_date) < new Date() 
                                    ? 'bg-red-500/20 text-red-400' 
                                    : 'bg-white/10 text-white/60'
                                }`}>
                                  Due: {formatDate(task.due_date)}
                                </span>
                              )}
                              <span className="text-xs px-2 py-1 rounded-lg bg-amber-500/20 text-amber-400">
                                Pending
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Completed Tasks */}
                {tasks.filter(t => t.status === 'completed').length > 0 && (
                  <div className="space-y-3 mt-6">
                    <h3 className="text-white/70 text-sm font-medium uppercase tracking-wide">Completed</h3>
                    {tasks.filter(t => t.status === 'completed').map((task) => (
                      <div
                        key={task.id}
                        className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-4 opacity-60"
                        data-testid={`task-item-${task.id}`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                            <Check className="w-5 h-5 text-emerald-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-medium line-through">{task.title}</h4>
                            {task.description && (
                              <p className="text-white/50 text-sm mt-1">{task.description}</p>
                            )}
                            <div className="flex items-center gap-4 mt-2">
                              {task.completed_at && (
                                <span className="text-xs px-2 py-1 rounded-lg bg-emerald-500/20 text-emerald-400">
                                  Completed: {formatDate(task.completed_at)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-12 text-center">
                <Check className="w-16 h-16 mx-auto mb-4 text-white/20" />
                <p className="text-white/50">No tasks assigned to you yet</p>
              </div>
            )}
          </div>
        )}

        {/* Forms Section */}
        {activeSection === 'forms' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <SectionHeader title="Planning Forms" icon={ClipboardList} color={djPrimaryColor} />
              {data.form_responses && data.form_responses.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handlePrintAllForms()}
                    className="border-white/20 text-white hover:bg-white/10"
                    data-testid="print-forms-btn"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Print All
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleEmailForms()}
                    className="border-white/20 text-white hover:bg-white/10"
                    data-testid="email-forms-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email to Me
                  </Button>
                </div>
              )}
            </div>
            
            {planning_forms && planning_forms.length > 0 ? (
              <div className="space-y-4">
                {planning_forms.map(form => {
                  const response = data.form_responses?.find(r => r.form_id === form.id);
                  const isCompleted = !!response;
                  const locked = isFormLocked(form);
                  
                  return (
                    <div
                      key={form.id}
                      onClick={() => openFormDialog(form, locked)}
                      className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-5 flex items-center justify-between cursor-pointer hover:bg-white/10 transition-all group"
                      data-testid={`form-item-${form.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center"
                          style={{ backgroundColor: locked ? '#f9731620' : isCompleted ? '#22c55e20' : djPrimaryColor + '20' }}
                        >
                          {locked ? <Lock className="w-5 h-5 text-orange-400" /> :
                           isCompleted ? <Check className="w-5 h-5 text-green-400" /> :
                           <ClipboardList className="w-5 h-5" style={{ color: djPrimaryColor }} />}
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{form.name}</h3>
                          <p className="text-white/50 text-sm">{form.fields?.length || 0} questions</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {isCompleted && (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handlePrintForm(form, response); }}
                              className="text-white/60 hover:text-white"
                              title="Print"
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <span 
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 ${
                            locked ? 'bg-white/10 text-white/60' : 
                            isCompleted ? 'bg-green-500/20 text-green-400' : 
                            'text-white'
                          }`}
                          style={!locked && !isCompleted ? { backgroundColor: djPrimaryColor } : {}}
                        >
                          {locked ? 'View' : isCompleted ? 'Edit' : 'Fill'}
                          <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8">
                <EmptyState
                  icon={ClipboardList}
                  title="No Forms Assigned"
                  description="Your DJ hasn't assigned any forms yet"
                  color={djPrimaryColor}
                />
              </div>
            )}
          </div>
        )}

        {/* Playlists Section */}
        {activeSection === 'music' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <SectionHeader title="Your Playlists" icon={Music} color={djSecondaryColor} />
              <div className="flex items-center gap-2">
                {playlists && playlists.length > 0 && (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => handlePrintAllPlaylists()}
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="print-playlists-btn"
                    >
                      <Printer className="w-4 h-4 mr-2" />
                      Print All
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => handleEmailPlaylists()}
                      className="border-white/20 text-white hover:bg-white/10"
                      data-testid="email-playlists-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      Email to Me
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAppleMusicImport(true);
                    setAppleMusicUrl('');
                    setAppleMusicImportResult(null);
                    setAppleMusicTargetPlaylist('new');
                  }}
                  className="border-white/20 text-white hover:bg-white/10"
                  data-testid="import-apple-music-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Import from Apple Music
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowYouTubeImport(true);
                    setYoutubeUrl('');
                    setYoutubeTargetPlaylist('new');
                  }}
                  className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                  data-testid="import-youtube-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Import from YouTube
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAIPlaylistDialog(true);
                    setAiPlaylistCriteria('');
                    setAiPlaylistName('');
                  }}
                  className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20"
                  data-testid="ai-playlist-btn"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI Playlist Generator
                </Button>
                {/* Lockout warning or last edit date note */}
                {editLockout.playlists_locked ? (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 mb-4">
                    <p className="text-amber-400 text-sm flex items-center gap-2">
                      <Lock className="w-4 h-4" />
                      {editLockout.message}
                    </p>
                  </div>
                ) : editLockout.last_edit_date && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                    <p className="text-blue-400 text-sm flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Last day to make changes: <span className="font-semibold">{editLockout.last_edit_date}</span>
                    </p>
                  </div>
                )}
                
                <Button
                  onClick={() => openPlaylistDialog()}
                  className="text-white"
                  style={{ backgroundColor: editLockout.playlists_locked ? '#4a5568' : djPrimaryColor }}
                  data-testid="create-playlist-btn"
                  disabled={editLockout.playlists_locked}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {editLockout.playlists_locked ? 'Editing Locked' : 'New Playlist'}
                </Button>
              </div>
            </div>
            
            {/* Helpful note about creating multiple playlists */}
            <div className="bg-white/5 border border-white/10 rounded-lg p-4">
              <p className="text-white/80 text-sm">
                <span className="font-medium text-white">Tip:</span> You can create separate playlists for different parts of your event, such as <span className="text-white font-medium">Cocktail Hour</span>, <span className="text-white font-medium">Dinner</span>, <span className="text-white font-medium">Open Dancing</span>, or <span className="text-white font-medium">Pre-Ceremony</span>. Click "New Playlist" to add as many as you need!
              </p>
            </div>
            
            {playlists && playlists.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {playlists.map(playlist => (
                  <div
                    key={playlist.id}
                    onClick={() => openPlaylistDialog(playlist)}
                    className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-5 cursor-pointer hover:bg-white/10 transition-all group"
                    data-testid={`playlist-${playlist.id}`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-white font-medium flex items-center gap-2">
                        {playlist.name}
                        <ChevronRight className="w-4 h-4 text-white/30 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h3>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handlePrintPlaylist(playlist); }} className="text-white/60 hover:text-white" title="Print">
                          <Printer className="w-4 h-4" />
                        </Button>
                        {!editLockout.playlists_locked && (
                          <>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openPlaylistDialog(playlist); }} className="text-white/60 hover:text-white" title="Edit">
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); deletePlaylist(playlist.id); }} className="text-red-400 hover:text-red-300" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {editLockout.playlists_locked && (
                          <Button size="sm" variant="ghost" className="text-white/60" title="View">
                            <Eye className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-white/50 text-sm">{playlist.songs?.length || 0} songs</p>
                    {/* Show song designation breakdown */}
                    {playlist.songs?.length > 0 && (
                      <div className="flex gap-3 mt-2 text-xs">
                        {playlist.songs.filter(s => (s.designation || 'must_play') === 'must_play').length > 0 && (
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-green-500"></span>
                            <span className="text-white/40">{playlist.songs.filter(s => (s.designation || 'must_play') === 'must_play').length}</span>
                          </span>
                        )}
                        {playlist.songs.filter(s => s.designation === 'play_if_possible').length > 0 && (
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                            <span className="text-white/40">{playlist.songs.filter(s => s.designation === 'play_if_possible').length}</span>
                          </span>
                        )}
                        {playlist.songs.filter(s => s.designation === 'do_not_play').length > 0 && (
                          <span className="flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-red-500"></span>
                            <span className="text-white/40">{playlist.songs.filter(s => s.designation === 'do_not_play').length}</span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8">
                <EmptyState
                  icon={Music}
                  title="No Playlists Yet"
                  description="Create a playlist to share your music preferences"
                  action={{ label: 'Create Playlist', onClick: () => openPlaylistDialog() }}
                  color={djSecondaryColor}
                />
              </div>
            )}

            {/* Guest Request System Section */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6 mt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-semibold flex items-center gap-2">
                  <Users className="w-5 h-5 text-pink-400" />
                  Guest Song Requests
                </h3>
                <Button
                  variant="outline"
                  onClick={() => setShowGuestRequestSetup(true)}
                  className="border-white/20 text-white hover:bg-white/10"
                  data-testid="setup-guest-requests-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  {guestRequestSettings.enabled ? 'Manage' : 'Set Up'}
                </Button>
              </div>

              {!guestRequestSettings.enabled ? (
                <div className="text-center py-6">
                  <Users className="w-12 h-12 text-white/20 mx-auto mb-3" />
                  <p className="text-white/60 mb-2">Let your guests request songs!</p>
                  <p className="text-white/40 text-sm mb-4">
                    Create a password-protected page where guests can submit and upvote song requests.
                  </p>
                  <Button
                    onClick={() => setShowGuestRequestSetup(true)}
                    className="text-white"
                    style={{ backgroundColor: djPrimaryColor }}
                    data-testid="enable-guest-requests-btn"
                  >
                    <Users className="w-4 h-4 mr-2" />
                    Enable Guest Requests
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Guest Link Info */}
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div>
                        <p className="text-green-400 text-sm font-medium flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          Guest requests enabled
                        </p>
                        <p className="text-white/50 text-xs mt-1">
                          Share this link with your guests so they can request songs
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const guestUrl = `${getBaseUrl()}/guest-requests/${guestRequestSettings.link_code}`;
                            navigator.clipboard.writeText(guestUrl);
                            toast.success('Link copied to clipboard!');
                          }}
                          className="border-white/20 text-white hover:bg-white/10"
                          data-testid="copy-guest-link-btn"
                        >
                          <Copy className="w-4 h-4 mr-1" />
                          Copy Link
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            window.open(`/guest-requests/${guestRequestSettings.link_code}`, '_blank');
                          }}
                          className="border-white/20 text-white hover:bg-white/10"
                          data-testid="open-guest-page-btn"
                        >
                          <ExternalLink className="w-4 h-4 mr-1" />
                          Preview
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowQrCode(!showQrCode)}
                          className={`border-white/20 text-white hover:bg-white/10 ${showQrCode ? 'bg-white/10' : ''}`}
                          data-testid="show-qr-code-btn"
                        >
                          <LayoutGrid className="w-4 h-4 mr-1" />
                          QR Code
                        </Button>
                      </div>
                    </div>
                    
                    {/* QR Code Section */}
                    {showQrCode && (
                      <div className="mt-4 flex flex-col items-center gap-3 p-4 bg-white rounded-xl" data-testid="qr-code-section">
                        <div ref={qrRef}>
                          <QRCodeCanvas
                            value={`${getBaseUrl()}/guest-requests/${guestRequestSettings.link_code}`}
                            size={200}
                            level="H"
                            includeMargin={true}
                          />
                        </div>
                        <p className="text-gray-500 text-xs text-center max-w-[220px]">
                          Guests scan this code to request songs
                        </p>
                        <Button
                          size="sm"
                          onClick={() => {
                            const canvas = qrRef.current?.querySelector('canvas');
                            if (canvas) {
                              const link = document.createElement('a');
                              link.download = 'guest-request-qr-code.png';
                              link.href = canvas.toDataURL('image/png');
                              link.click();
                              toast.success('QR code downloaded!');
                            }
                          }}
                          className="bg-gray-900 hover:bg-gray-800 text-white"
                          data-testid="download-qr-btn"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download QR Code
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Pending Requests */}
                  {guestRequests.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-white/80 text-sm font-medium">
                          Pending Requests ({guestRequests.filter(r => r.status === 'pending').length})
                        </h4>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={fetchGuestRequests}
                          className="text-white/50 hover:text-white"
                        >
                          <Search className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      {guestRequests.filter(r => r.status === 'pending').length === 0 ? (
                        <p className="text-white/40 text-sm text-center py-4">
                          No pending requests. Approved songs have been added to your playlists.
                        </p>
                      ) : (
                        guestRequests.filter(r => r.status === 'pending').map(request => (
                          <div
                            key={request.id}
                            className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10"
                            data-testid={`guest-request-${request.id}`}
                          >
                            <div className="flex-1">
                              <p className="text-white font-medium">{request.song_title}</p>
                              <p className="text-white/60 text-sm">{request.artist}</p>
                              <p className="text-white/40 text-xs mt-1">
                                Requested by {request.guest_name}
                                {request.notes && <span className="ml-2 italic">"{request.notes}"</span>}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1 text-pink-400">
                                <ThumbsUp className="w-4 h-4" />
                                <span className="text-sm">{request.upvote_count || 0}</span>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setRequestToApprove(request);
                                    setSelectedApprovePlaylist('');
                                    setShowApproveDialog(true);
                                  }}
                                  className="bg-green-600 hover:bg-green-700 text-white"
                                  data-testid={`approve-request-${request.id}`}
                                >
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => rejectGuestRequest(request.id)}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                                  data-testid={`reject-request-${request.id}`}
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))
                      )}

                      {/* Show approved/rejected summary */}
                      {guestRequests.filter(r => r.status !== 'pending').length > 0 && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <p className="text-white/50 text-xs">
                            {guestRequests.filter(r => r.status === 'approved').length} approved • 
                            {guestRequests.filter(r => r.status === 'rejected').length} rejected
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-white/40 text-sm">
                        No song requests yet. Share the link with your guests!
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contracts Section */}
        {activeSection === 'contracts' && (
          <div className="space-y-6">
            <SectionHeader title="Contracts" icon={FileText} color="#8b5cf6" />
            
            {contracts?.length > 0 ? (
              <div className="space-y-4">
                {contracts.map((contractItem, index) => (
                  <div 
                    key={contractItem.id} 
                    className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-6"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-white">
                          Contract {contracts.length > 1 ? `#${index + 1}` : ''}
                        </h3>
                        <p className="text-white/50 text-sm">
                          Created: {new Date(contractItem.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        contractItem.status === 'fully_signed' 
                          ? 'bg-green-500/20 text-green-400' 
                          : contractItem.client_signed_at 
                            ? 'bg-amber-500/20 text-amber-400' 
                            : 'bg-white/10 text-white/70'
                      }`}>
                        {contractItem.status === 'fully_signed' ? 'Fully Signed' :
                         contractItem.client_signed_at ? 'Awaiting Counter-Sign' : 'Needs Signature'}
                      </span>
                    </div>
                    
                    {contractItem.client_signed_at && (
                      <p className="text-white/50 text-sm mb-2">
                        Signed by you: {new Date(contractItem.client_signed_at).toLocaleDateString()}
                      </p>
                    )}
                    
                    {contractItem.company_signed_at && (
                      <p className="text-white/50 text-sm mb-2">
                        Counter-signed: {new Date(contractItem.company_signed_at).toLocaleDateString()}
                      </p>
                    )}
                    
                    <div className="flex gap-3 mt-4">
                      <Button
                        onClick={() => openViewContractDialog(contractItem)}
                        className="flex-1"
                        style={{ backgroundColor: djPrimaryColor }}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View
                      </Button>
                      <Button
                        onClick={() => downloadContract(contractItem)}
                        variant="outline"
                        className="flex-1 border-white/20 text-white hover:bg-white/10"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                      {!contractItem.client_signed_at && (
                        <Button
                          onClick={() => {
                            setSelectedContract(contractItem);
                            setShowSignContractDialog(true);
                          }}
                          className="flex-1 bg-green-600 hover:bg-green-700"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Sign
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 mx-auto text-white/20 mb-4" />
                <p className="text-white/50">No contracts available</p>
              </div>
            )}
          </div>
        )}

        {/* Payments Section */}
        {activeSection === 'payments' && (
          <div className="space-y-6">
            <SectionHeader title="Payments & Billing" icon={CreditCard} color="#22c55e" />
            
            {/* Payment Summary */}
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-5 text-center">
                <p className="text-white/50 text-sm mb-1">Total</p>
                <p className="text-white text-2xl font-bold">${(booking.total_amount || 0).toFixed(2)}</p>
              </div>
              <div className="bg-green-500/10 rounded-2xl border border-green-500/30 p-5 text-center">
                <p className="text-green-400/70 text-sm mb-1">Paid</p>
                <p className="text-green-400 text-2xl font-bold">${(booking.paid_amount || 0).toFixed(2)}</p>
              </div>
              <div className={`rounded-2xl border p-5 text-center ${balance > 0 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-green-500/10 border-green-500/30'}`}>
                <p className={`text-sm mb-1 ${balance > 0 ? 'text-amber-400/70' : 'text-green-400/70'}`}>Balance Due</p>
                <p className={`text-2xl font-bold ${balance > 0 ? 'text-amber-400' : 'text-green-400'}`}>${balance.toFixed(2)}</p>
              </div>
            </div>
            
            {/* Payment Schedule */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Calendar className="w-5 h-5" style={{ color: djPrimaryColor }} />
                Payment Options
              </h3>
              
              <div className="space-y-3">
                {/* Pay in Full Option - Show only if no payments made yet */}
                {(() => {
                  const payInFullOption = (booking.payment_schedule || []).find(p => p.payment_type === 'pay_in_full');
                  const hasAnyPayments = (booking.paid_amount || 0) > 0;
                  
                  if (payInFullOption && !hasAnyPayments && balance > 0) {
                    const discountAmount = (booking.total_amount || 0) - parseFloat(payInFullOption.amount || 0);
                    return (
                      <div className="p-4 rounded-xl bg-purple-500/10 border-2 border-purple-500/50 mb-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                              <Sparkles className="w-6 h-6 text-purple-400" />
                            </div>
                            <div>
                              <p className="font-semibold text-purple-300 flex items-center gap-2">
                                {payInFullOption.description || 'Pay in Full & Save'}
                                <span className="text-xs bg-purple-500/30 text-purple-300 px-2 py-0.5 rounded">
                                  SAVE ${discountAmount.toFixed(2)}
                                </span>
                              </p>
                              <p className="text-white/50 text-sm">
                                {payInFullOption.due_date ? `Offer valid until ${formatDate(payInFullOption.due_date)}` : 'Limited time offer'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <span className="text-lg font-bold text-purple-300">
                                ${parseFloat(payInFullOption.amount || 0).toFixed(2)}
                              </span>
                              <p className="text-xs text-white/40 line-through">
                                ${(booking.total_amount || 0).toFixed(2)}
                              </p>
                            </div>
                            <Button
                              onClick={() => handleScheduledPayment(parseFloat(payInFullOption.amount), payInFullOption.description || 'Pay in Full')}
                              className="bg-purple-500 hover:bg-purple-600 text-white"
                              data-testid="pay-in-full-btn"
                            >
                              Pay in Full
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {/* Retainer Payment - Required minimum */}
                {(() => {
                  const retainerPayment = (booking.payment_schedule || []).find(p => p.payment_type === 'retainer');
                  // Also check legacy retainer_amount field
                  const legacyRetainer = booking.retainer_amount || booking.deposit_amount;
                  
                  if (retainerPayment) {
                    const isPaid = retainerPayment.paid || (booking.paid_amount || 0) >= parseFloat(retainerPayment.amount || 0);
                    return (
                      <div className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                        isPaid 
                          ? 'bg-green-500/10 border border-green-500/30' 
                          : 'bg-blue-500/10 border-2 border-blue-500/50'
                      }`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isPaid ? 'bg-green-500/20' : 'bg-blue-500/20'
                          }`}>
                            {isPaid ? (
                              <Check className="w-5 h-5 text-green-400" />
                            ) : (
                              <DollarSign className="w-5 h-5 text-blue-400" />
                            )}
                          </div>
                          <div>
                            <p className={`font-medium flex items-center gap-2 ${isPaid ? 'text-green-400' : 'text-white'}`}>
                              {retainerPayment.description || 'Retainer'}
                              {!isPaid && <span className="text-xs bg-blue-500/30 text-blue-300 px-2 py-0.5 rounded">Required</span>}
                            </p>
                            <p className="text-white/50 text-sm">
                              {retainerPayment.due_date ? `Due ${formatDate(retainerPayment.due_date)}` : 'Due upon booking'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`text-lg font-bold ${isPaid ? 'text-green-400' : 'text-white'}`}>
                            ${parseFloat(retainerPayment.amount || 0).toFixed(2)}
                          </span>
                          {isPaid ? (
                            <span className="px-4 py-2 rounded-xl bg-green-500/20 text-green-400 text-sm font-medium">
                              Paid
                            </span>
                          ) : (
                            <Button
                              onClick={() => handleScheduledPayment(parseFloat(retainerPayment.amount), retainerPayment.description || 'Retainer')}
                              className="text-white"
                              style={{ backgroundColor: djPrimaryColor }}
                              data-testid="pay-retainer-btn"
                            >
                              Pay Now
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  } else if (legacyRetainer > 0) {
                    // Handle legacy retainer field
                    const isPaid = isPaymentMade('retainer', legacyRetainer);
                    return (
                      <PaymentScheduleItem
                        label="Retainer / Deposit"
                        amount={legacyRetainer}
                        dueDate="Due upon booking"
                        isPaid={isPaid}
                        onPayClick={() => handleScheduledPayment(legacyRetainer, 'Retainer / Deposit')}
                        paymentEnabled={paymentSettings?.payment_enabled}
                        primaryColor={djPrimaryColor}
                      />
                    );
                  }
                  return null;
                })()}
                
                {/* Regular Scheduled Payments - Allow custom amounts */}
                {booking.payment_schedule && booking.payment_schedule.length > 0 ? (
                  booking.payment_schedule
                    .filter(p => p.payment_type !== 'retainer' && p.payment_type !== 'pay_in_full')
                    .map((schedPayment, idx) => {
                      const isPaid = schedPayment.paid || isPaymentMade(schedPayment.description, parseFloat(schedPayment.amount));
                      const allowPartial = schedPayment.allow_partial !== false;
                      
                      return (
                        <div 
                          key={idx}
                          className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                            isPaid 
                              ? 'bg-green-500/10 border border-green-500/30' 
                              : 'bg-white/5 border border-white/10 hover:bg-white/10'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              isPaid ? 'bg-green-500/20' : 'bg-white/10'
                            }`}>
                              {isPaid ? (
                                <Check className="w-5 h-5 text-green-400" />
                              ) : (
                                <DollarSign className="w-5 h-5 text-white/50" />
                              )}
                            </div>
                            <div>
                              <p className={`font-medium ${isPaid ? 'text-green-400' : 'text-white'}`}>
                                {schedPayment.description || `Payment ${idx + 1}`}
                              </p>
                              <p className="text-white/50 text-sm">
                                {schedPayment.due_date ? `Due ${formatDate(schedPayment.due_date)}` : 'No due date'}
                                {allowPartial && !isPaid && ' • Partial payments accepted'}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`text-lg font-bold ${isPaid ? 'text-green-400' : 'text-white'}`}>
                              ${parseFloat(schedPayment.amount || 0).toFixed(2)}
                            </span>
                            {isPaid ? (
                              <span className="px-4 py-2 rounded-xl bg-green-500/20 text-green-400 text-sm font-medium">
                                Paid
                              </span>
                            ) : (
                              <Button
                                onClick={() => handleScheduledPayment(parseFloat(schedPayment.amount), schedPayment.description || `Payment ${idx + 1}`, allowPartial)}
                                className="text-white"
                                style={{ backgroundColor: djPrimaryColor }}
                              >
                                {allowPartial ? 'Make Payment' : 'Pay Now'}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })
                ) : (
                  !booking.retainer_amount && !booking.deposit_amount && !(booking.payment_schedule || []).some(p => p.payment_type === 'retainer') && (
                    <div className="text-center py-6">
                      <p className="text-white/50">No payment schedule set</p>
                      <p className="text-white/30 text-sm mt-1">Contact your DJ for payment details</p>
                    </div>
                  )
                )}
                
                {/* Final Balance (if not covered by schedule) */}
                {(() => {
                  const retainerTotal = (booking.payment_schedule || [])
                    .filter(p => p.payment_type === 'retainer')
                    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0) || 
                    (booking.retainer_amount || booking.deposit_amount || 0);
                  const scheduledTotal = retainerTotal + 
                    (booking.payment_schedule || [])
                    .filter(p => p.payment_type !== 'retainer' && p.payment_type !== 'pay_in_full')
                    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                  const remainingBalance = (booking.total_amount || 0) - scheduledTotal;
                  
                  if (remainingBalance > 0 && balance > 0) {
                    const isPaid = balance <= 0;
                    return (
                      <div className={`flex items-center justify-between p-4 rounded-xl transition-all ${
                        isPaid 
                          ? 'bg-green-500/10 border border-green-500/30' 
                          : 'bg-white/5 border border-white/10 hover:bg-white/10'
                      }`}>
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isPaid ? 'bg-green-500/20' : 'bg-white/10'
                          }`}>
                            {isPaid ? (
                              <Check className="w-5 h-5 text-green-400" />
                            ) : (
                              <DollarSign className="w-5 h-5 text-white/50" />
                            )}
                          </div>
                          <div>
                            <p className={`font-medium ${isPaid ? 'text-green-400' : 'text-white'}`}>Final Balance</p>
                            <p className="text-white/50 text-sm">Due before event • Partial payments accepted</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className={`text-lg font-bold ${isPaid ? 'text-green-400' : 'text-white'}`}>
                            ${balance.toFixed(2)}
                          </span>
                          {!isPaid && (
                            <Button
                              onClick={() => handleScheduledPayment(balance, 'Final Balance', true)}
                              className="text-white"
                              style={{ backgroundColor: djPrimaryColor }}
                            >
                              Make Payment
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
            
            {/* Invoices */}
            {invoices && invoices.length > 0 && (
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6">
                <h3 className="text-white font-semibold mb-4">Invoices</h3>
                <div className="space-y-3">
                  {invoices.map(invoice => (
                    <div 
                      key={invoice.id}
                      className="flex items-center justify-between p-4 bg-white/5 rounded-xl"
                      data-testid={`invoice-${invoice.id}`}
                    >
                      <div>
                        <p className="text-white font-medium">{invoice.invoice_number}</p>
                        <p className="text-white/50 text-sm">Due: {formatDate(invoice.due_date)}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-white font-medium">${invoice.amount}</span>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={() => viewInvoice(invoice)} className="text-white/60 hover:text-white">
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => downloadInvoice(invoice)} className="text-white/60 hover:text-white">
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Payment History */}
            {payments && payments.length > 0 && (
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6">
                <h3 className="text-white font-semibold mb-4">Payment History</h3>
                <div className="space-y-3">
                  {payments.map(payment => (
                    <div key={payment.id} className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                      <div>
                        <p className="text-white font-medium">${payment.amount}</p>
                        <p className="text-white/50 text-sm">{formatDate(payment.created_at || payment.date)}</p>
                        {payment.description && <p className="text-white/40 text-xs mt-1">{payment.description}</p>}
                      </div>
                      <span className="text-xs px-3 py-1 rounded-full bg-green-500/20 text-green-400">
                        {payment.status || 'Completed'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Messages Section */}
        {activeSection === 'messages' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <SectionHeader title="Messages" icon={MessageSquare} color="#06b6d4" />
              <Button
                onClick={() => openEmailDialog()}
                className="text-white"
                style={{ backgroundColor: djPrimaryColor }}
                data-testid="compose-email-btn"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Email
              </Button>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6">
              {/* Quick Message */}
              <div className="flex gap-3 mb-6">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Send a quick message..."
                  className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  data-testid="quick-message-input"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !newMessage.trim()}
                  className="text-white"
                  style={{ backgroundColor: djPrimaryColor }}
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Messages List */}
              {messages && messages.length > 0 ? (
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`p-4 rounded-xl ${msg.sender_type === 'client' ? 'bg-white/10 ml-8' : 'bg-white/5 mr-8'}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-white/60 text-xs">
                          {msg.sender_type === 'client' ? 'You' : data?.settings?.company_name || 'DJ'}
                        </span>
                        <span className="text-white/30 text-xs">
                          {new Date(msg.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-white text-sm">{msg.content}</p>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              ) : (
                <EmptyState
                  icon={MessageSquare}
                  title="No Messages"
                  description="Start a conversation"
                  color="#06b6d4"
                />
              )}
            </div>
            
            {/* Email Communications */}
            {emailCommunications && emailCommunications.length > 0 && (
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6">
                <h3 className="text-white font-semibold mb-4">Email History</h3>
                <div className="space-y-3">
                  {emailCommunications.map(email => (
                    <div
                      key={email.id}
                      className="p-4 bg-white/5 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
                      onClick={() => toggleEmailExpand(email.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {email.sender_type === 'client' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">You</span>
                          )}
                          <span className="text-white font-medium">{email.subject}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-white/40 text-xs">
                            {new Date(email.created_at).toLocaleDateString()}
                          </span>
                          {expandedEmails[email.id] ? <ChevronUp className="w-4 h-4 text-white/40" /> : <ChevronDown className="w-4 h-4 text-white/40" />}
                        </div>
                      </div>
                      {expandedEmails[email.id] && (
                        <div className="mt-4 pt-4 border-t border-white/10">
                          <div className="text-white/70 text-sm whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: email.body }} />
                          {email.sender_type !== 'client' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={(e) => { e.stopPropagation(); openEmailDialog(email); }}
                              className="mt-4 border-white/20 text-white hover:bg-white/10"
                            >
                              Reply
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Documents Section */}
        {activeSection === 'documents' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <SectionHeader title="Documents" icon={File} color="#8b5cf6" />
              <div className="flex items-center gap-2">
                {documents && documents.length > 0 && (
                  <Button
                    variant="outline"
                    onClick={() => handleEmailDocumentsList()}
                    className="border-white/20 text-white hover:bg-white/10"
                    data-testid="email-docs-list-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Email List to Me
                  </Button>
                )}
                <Button
                  onClick={() => setShowDocUploadDialog(true)}
                  className="text-white"
                  style={{ backgroundColor: djPrimaryColor }}
                  data-testid="upload-document-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </Button>
              </div>
            </div>
            
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6">
              {documents && documents.length > 0 ? (
                <div className="space-y-3">
                  {documents.map(doc => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-4 bg-white/5 rounded-xl"
                      data-testid={`document-${doc.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-white/10">
                          <File className="w-5 h-5" style={{ color: djPrimaryColor }} />
                        </div>
                        <div>
                          <p className="text-white font-medium">{doc.original_filename}</p>
                          <p className="text-white/50 text-xs">{formatFileSize(doc.file_size)}</p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => window.open(doc.file_url, '_blank')} className="text-white/60 hover:text-white" title="View">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDownloadDocument(doc)} className="text-white/60 hover:text-white" title="Download">
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handlePrintDocument(doc)} className="text-white/60 hover:text-white" title="Print">
                          <Printer className="w-4 h-4" />
                        </Button>
                        {doc.uploaded_by === 'client' && (
                          <Button size="sm" variant="ghost" onClick={() => handleDeleteDocument(doc.id)} className="text-red-400 hover:text-red-300" title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={File}
                  title="No Documents"
                  description="Upload documents to share with your DJ"
                  action={{ label: 'Upload', onClick: () => setShowDocUploadDialog(true) }}
                  color="#8b5cf6"
                />
              )}
            </div>
          </div>
        )}

        {/* Add-ons Section */}
        {activeSection === 'addons' && (
          <div className="space-y-6">
            <SectionHeader title="Available Add-ons" icon={Plus} color={djSecondaryColor} />
            
            {availableAddons && availableAddons.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {availableAddons.map(addon => {
                  const isPending = pendingAddonRequests.includes(addon.id);
                  const historyItem = addonRequestHistory.find(r => r.addon_id === addon.id);
                  
                  return (
                    <div
                      key={addon.id}
                      className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden"
                      data-testid={`addon-${addon.id}`}
                    >
                      {/* Add-on Image */}
                      {addon.image_url && (
                        <div className="w-full h-40 overflow-hidden">
                          <img 
                            src={addon.image_url} 
                            alt={addon.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div className="p-5">
                        <div className="flex justify-between mb-3">
                          <h3 className="text-white font-medium">{addon.name}</h3>
                          <span className="font-bold" style={{ color: djPrimaryColor }}>${addon.price}</span>
                        </div>
                        {addon.description && (
                          <p className="text-white/50 text-sm mb-4">{addon.description}</p>
                        )}
                        {isPending ? (
                          <span className="text-xs px-3 py-1 rounded-full bg-amber-500/20 text-amber-400">Request Pending</span>
                        ) : historyItem?.status === 'approved' ? (
                          <span className="text-xs px-3 py-1 rounded-full bg-green-500/20 text-green-400">Added</span>
                        ) : historyItem?.status === 'declined' ? (
                          <span className="text-xs px-3 py-1 rounded-full bg-red-500/20 text-red-400">Declined</span>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => setRequestingAddon(addon)}
                            className="text-white"
                            style={{ backgroundColor: djPrimaryColor }}
                            data-testid={`request-addon-btn-${addon.id}`}
                          >
                            Request Add-on
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-8">
                <EmptyState
                  icon={Plus}
                  title="No Add-ons Available"
                  description="Check back later for available upgrades"
                  color={djSecondaryColor}
                />
              </div>
            )}
          </div>
        )}

        {/* Vendors Section */}
        {activeSection === 'vendors' && (
          <div className="space-y-6" data-testid="vendors-section">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <SectionHeader title="Event Vendors" icon={Building2} color={djSecondaryColor} />
              <Button
                onClick={() => setShowAddVendorDialog(true)}
                className="text-white"
                style={{ backgroundColor: djPrimaryColor }}
                data-testid="add-vendor-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Vendor
              </Button>
            </div>
            
            {vendors && vendors.length > 0 ? (
              <div className="grid gap-4 md:grid-cols-2">
                {vendors.map((vendor) => (
                  <div
                    key={vendor.id}
                    className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-5 hover:border-white/20 transition-colors"
                    data-testid={`vendor-${vendor.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-white font-medium text-lg">{vendor.name}</h3>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-white/60">
                          {vendor.category}
                        </span>
                      </div>
                    </div>
                    
                    {vendor.contact_name && (
                      <p className="text-white/60 text-sm mb-1">Contact: {vendor.contact_name}</p>
                    )}
                    
                    {vendor.phone && (
                      <p className="text-white/60 text-sm mb-1">
                        <Phone className="w-3 h-3 inline mr-1" />
                        {vendor.phone}
                      </p>
                    )}
                    
                    {vendor.email && (
                      <div className="mt-4">
                        <Button
                          onClick={() => openVendorEmailDialog(vendor)}
                          className="w-full text-white"
                          style={{ backgroundColor: djPrimaryColor }}
                          data-testid={`email-vendor-${vendor.id}`}
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Send Email
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-12 text-center">
                <Building2 className="w-16 h-16 mx-auto mb-4 text-white/20" />
                <p className="text-white/50 mb-4">No vendors have been added yet</p>
                <Button
                  onClick={() => setShowAddVendorDialog(true)}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Your First Vendor
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Guest Experience Section */}
        {activeSection === 'experience' && (
          <div className="space-y-6" data-testid="experience-section">
            <SectionHeader title="Guest Experience" icon={PartyPopper} color="#e91e63" />
            <EventExperienceSection token={token} booking={booking} portalUrl={window.location.origin} />
          </div>
        )}

        {/* Settings Section */}
        {activeSection === 'settings' && (
          <div className="space-y-6">
            <SectionHeader title="Settings" icon={Settings} color="#64748b" />
            
            {/* Contact Info */}
            <ContactInfoCard booking={data?.booking} token={token} setBooking={(updater) => setData(prev => ({ ...prev, booking: typeof updater === 'function' ? updater(prev.booking) : updater }))} />

            {/* Theme Settings */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6 space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-purple-500/20">
                  {clientTheme === 'light' ? <Sun className="w-5 h-5 text-purple-400" /> : <Moon className="w-5 h-5 text-purple-400" />}
                </div>
                <div>
                  <h3 className="text-white font-semibold">Appearance</h3>
                  <p className="text-white/50 text-sm">Choose your preferred theme</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={() => handleThemeChange('light')}
                  className={`p-4 rounded-2xl border-2 transition-all flex items-center gap-3 ${
                    clientTheme === 'light' 
                      ? 'border-purple-500 bg-purple-500/20' 
                      : 'border-white/10 hover:border-white/30 bg-white/5'
                  }`}
                  data-testid="theme-light-btn"
                >
                  <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center">
                    <Sun className="w-6 h-6 text-amber-500" />
                  </div>
                  <div className="text-left">
                    <p className={`font-medium ${clientTheme === 'light' ? 'text-purple-400' : 'text-white'}`}>Light</p>
                    <p className="text-white/50 text-sm">Bright theme</p>
                  </div>
                  {clientTheme === 'light' && <Check className="w-5 h-5 text-purple-400 ml-auto" />}
                </button>
                
                <button
                  onClick={() => handleThemeChange('dark')}
                  className={`p-4 rounded-2xl border-2 transition-all flex items-center gap-3 ${
                    clientTheme === 'dark' 
                      ? 'border-purple-500 bg-purple-500/20' 
                      : 'border-white/10 hover:border-white/30 bg-white/5'
                  }`}
                  data-testid="theme-dark-btn"
                >
                  <div className="w-12 h-12 rounded-xl bg-slate-800 flex items-center justify-center border border-white/10">
                    <Moon className="w-6 h-6 text-blue-400" />
                  </div>
                  <div className="text-left">
                    <p className={`font-medium ${clientTheme === 'dark' ? 'text-purple-400' : 'text-white'}`}>Dark</p>
                    <p className="text-white/50 text-sm">Easy on eyes</p>
                  </div>
                  {clientTheme === 'dark' && <Check className="w-5 h-5 text-purple-400 ml-auto" />}
                </button>
              </div>
            </div>
            
            {/* Security Settings */}
            <div className="bg-white/5 backdrop-blur-xl rounded-3xl border border-white/10 p-6 space-y-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-amber-500/20">
                  <Key className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold">Security</h3>
                  <p className="text-white/50 text-sm">Manage your account security</p>
                </div>
              </div>
              
              <button
                onClick={() => setShowChangePasswordDialog(true)}
                className="w-full p-4 rounded-2xl border border-white/10 hover:border-white/30 bg-white/5 hover:bg-white/10 transition-all flex items-center justify-between"
                data-testid="change-password-btn"
              >
                <div className="flex items-center gap-3">
                  <Lock className="w-5 h-5 text-white/60" />
                  <div className="text-left">
                    <p className="text-white font-medium">Change Password</p>
                    <p className="text-white/50 text-sm">Update your portal password</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-white/40" />
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Dialogs */}
      {/* Venue Image Upload Dialog */}
      <Dialog open={showVenueImageUpload} onOpenChange={setShowVenueImageUpload}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Upload Venue Photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div 
              className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-white/40 transition-colors"
              onClick={() => venueImageInputRef.current?.click()}
            >
              <input
                ref={venueImageInputRef}
                type="file"
                accept="image/*"
                onChange={handleVenueImageSelect}
                className="hidden"
              />
              {venueImage ? (
                <p className="text-white">{venueImage.name}</p>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto mb-2 text-white/40" />
                  <p className="text-white/60">Click to select an image</p>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVenueImageUpload(false)} className="border-white/20 text-white">
                Cancel
              </Button>
              <Button
                onClick={handleUploadVenueImage}
                disabled={uploadingVenueImage || !venueImage}
                style={{ backgroundColor: djPrimaryColor }}
                className="text-white"
              >
                {uploadingVenueImage ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Venue Change Dialog */}
      <Dialog open={showVenueChangeDialog} onOpenChange={setShowVenueChangeDialog}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle>Request Venue Change</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Label className="text-white/70">Venue Name *</Label>
              <div className="relative">
                <Input
                  value={venueChangeForm.name}
                  onChange={(e) => {
                    setVenueChangeForm({ ...venueChangeForm, name: e.target.value });
                    searchVenues(e.target.value);
                  }}
                  placeholder="Search or enter venue name"
                  className="bg-white/10 border-white/20 text-white mt-1"
                  data-testid="venue-change-name-input"
                />
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5 w-4 h-4 text-white/40" />
              </div>
              {/* Venue Search Results */}
              {showVenueSearchResults && (
                <div className="absolute left-0 right-0 z-10 mt-1 bg-slate-800 border border-white/20 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                  {searchingVenues ? (
                    <p className="p-3 text-white/50 text-sm">Searching...</p>
                  ) : venueSearchResults.length > 0 ? (
                    venueSearchResults.map((venue, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectVenueFromSearch(venue)}
                        className="w-full text-left p-3 hover:bg-white/10 transition-colors border-b border-white/10 last:border-0"
                      >
                        <p className="text-white font-medium text-sm">{venue.name}</p>
                        {venue.address && (
                          <p className="text-white/50 text-xs">{[venue.address, venue.city, venue.state].filter(Boolean).join(', ')}</p>
                        )}
                      </button>
                    ))
                  ) : venueChangeForm.name.length >= 2 ? (
                    <p className="p-3 text-white/50 text-sm">No existing venues found - enter new venue details below</p>
                  ) : null}
                </div>
              )}
            </div>
            <div>
              <Label className="text-white/70">Street Address *</Label>
              <Input
                value={venueChangeForm.address}
                onChange={(e) => setVenueChangeForm({ ...venueChangeForm, address: e.target.value })}
                placeholder="123 Main Street"
                className="bg-white/10 border-white/20 text-white mt-1"
                data-testid="venue-change-address-input"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-white/70">City</Label>
                <Input
                  value={venueChangeForm.city}
                  onChange={(e) => setVenueChangeForm({ ...venueChangeForm, city: e.target.value })}
                  placeholder="City"
                  className="bg-white/10 border-white/20 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">State</Label>
                <Input
                  value={venueChangeForm.state}
                  onChange={(e) => setVenueChangeForm({ ...venueChangeForm, state: e.target.value })}
                  placeholder="State"
                  className="bg-white/10 border-white/20 text-white mt-1"
                />
              </div>
              <div>
                <Label className="text-white/70">ZIP</Label>
                <Input
                  value={venueChangeForm.zip_code}
                  onChange={(e) => setVenueChangeForm({ ...venueChangeForm, zip_code: e.target.value })}
                  placeholder="12345"
                  className="bg-white/10 border-white/20 text-white mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-white/70">Notes (optional)</Label>
              <textarea
                value={venueChangeForm.notes}
                onChange={(e) => setVenueChangeForm({ ...venueChangeForm, notes: e.target.value })}
                placeholder="Any additional information..."
                className="w-full mt-1 p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 min-h-[80px]"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowVenueChangeDialog(false)} className="border-white/20 text-white">
                Cancel
              </Button>
              <Button
                onClick={handleSubmitVenueChange}
                disabled={submittingVenueChange}
                style={{ backgroundColor: djPrimaryColor }}
                className="text-white"
                data-testid="submit-venue-change-btn"
              >
                {submittingVenueChange ? 'Submitting...' : 'Submit Request'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Timeline Event Dialog */}
      <Dialog open={showTimelineDialog} onOpenChange={setShowTimelineDialog}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Add Timeline Event</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/70">Time *</Label>
              <Input
                type="time"
                value={newTimelineEvent.time}
                onChange={(e) => setNewTimelineEvent({ ...newTimelineEvent, time: e.target.value })}
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white/70">Event Title *</Label>
              <Input
                value={newTimelineEvent.title}
                onChange={(e) => setNewTimelineEvent({ ...newTimelineEvent, title: e.target.value })}
                placeholder="e.g., First Dance"
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white/70">Description (optional)</Label>
              <textarea
                value={newTimelineEvent.description}
                onChange={(e) => setNewTimelineEvent({ ...newTimelineEvent, description: e.target.value })}
                placeholder="Additional details..."
                className="w-full mt-1 p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 min-h-[80px]"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTimelineDialog(false)} className="border-white/20 text-white">
                Cancel
              </Button>
              <Button
                onClick={saveTimelineEvent}
                disabled={savingTimeline}
                style={{ backgroundColor: djPrimaryColor }}
                className="text-white"
              >
                {savingTimeline ? 'Adding...' : 'Add Event'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Make a Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-white/60 text-sm">Balance Due</p>
              <p className="text-2xl font-bold text-white">${balance.toFixed(2)}</p>
            </div>
            
            {paymentSettings?.payment_enabled ? (
              <>
                <div>
                  <Label className="text-white/70">Payment Amount</Label>
                  <Input
                    type="number"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(e.target.value)}
                    placeholder="0.00"
                    min="0"
                    max={balance}
                    step="0.01"
                    className="bg-white/10 border-white/20 text-white mt-1"
                  />
                </div>
                {paymentSettings && !paymentSettings.deposit_met && (
                  <p className="text-amber-400 text-sm">
                    Minimum payment of ${paymentSettings.minimum_payment?.toFixed(2)} required
                  </p>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowPaymentDialog(false)} className="border-white/20 text-white">
                    Cancel
                  </Button>
                  <Button
                    onClick={handleMakePayment}
                    disabled={processingPayment}
                    style={{ backgroundColor: '#22c55e' }}
                    className="text-white"
                  >
                    {processingPayment ? 'Processing...' : `Pay $${paymentAmount || '0.00'}`}
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Mail className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-400 font-medium">Contact for Payment</span>
                  </div>
                  <p className="text-white/70 text-sm">
                    Online payments are not currently enabled. Please contact {data?.settings?.company_name || 'your DJ'} directly to make a payment.
                  </p>
                  {data?.settings?.email && (
                    <a 
                      href={`mailto:${data.settings.email}?subject=Payment for ${booking.event_type} - ${booking.client_name}&body=Hi, I would like to make a payment of $${paymentAmount || balance.toFixed(2)} for my event.`}
                      className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      Email {data?.settings?.company_name || 'DJ'}
                    </a>
                  )}
                  {data?.settings?.phone && (
                    <p className="text-white/50 text-sm mt-2">
                      Or call: <a href={`tel:${data.settings.phone}`} className="text-white underline">{data.settings.phone}</a>
                    </p>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowPaymentDialog(false)} className="border-white/20 text-white">
                    Close
                  </Button>
                </DialogFooter>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={showDocUploadDialog} onOpenChange={setShowDocUploadDialog}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div 
              className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-white/40 transition-colors"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
              />
              {selectedFile ? (
                <p className="text-white">{selectedFile.name}</p>
              ) : (
                <>
                  <Upload className="w-8 h-8 mx-auto mb-2 text-white/40" />
                  <p className="text-white/60">Click to select a file</p>
                </>
              )}
            </div>
            <div>
              <Label className="text-white/70">Note (optional)</Label>
              <Input
                value={docNote}
                onChange={(e) => setDocNote(e.target.value)}
                placeholder="What is this document?"
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDocUploadDialog(false)} className="border-white/20 text-white">
                Cancel
              </Button>
              <Button
                onClick={handleUploadDocument}
                disabled={uploadingDoc || !selectedFile}
                style={{ backgroundColor: djPrimaryColor }}
                className="text-white"
              >
                {uploadingDoc ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Compose Dialog */}
      <Dialog open={showComposeEmailDialog} onOpenChange={setShowComposeEmailDialog}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>{replyingTo ? 'Reply to Email' : 'Send Email'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/70">Subject</Label>
              <Input
                value={emailForm.subject}
                onChange={(e) => setEmailForm({ ...emailForm, subject: e.target.value })}
                placeholder="Email subject"
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-white/70">Message</Label>
              <textarea
                value={emailForm.body}
                onChange={(e) => setEmailForm({ ...emailForm, body: e.target.value })}
                placeholder="Your message..."
                className="w-full mt-1 p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 min-h-[150px]"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowComposeEmailDialog(false)} className="border-white/20 text-white">
                Cancel
              </Button>
              <Button
                onClick={sendClientEmail}
                disabled={sendingEmail}
                style={{ backgroundColor: djPrimaryColor }}
                className="text-white"
              >
                {sendingEmail ? 'Sending...' : 'Send Email'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add-on Request Dialog */}
      <Dialog open={!!requestingAddon} onOpenChange={(open) => !open && setRequestingAddon(null)}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Request Add-on</DialogTitle>
          </DialogHeader>
          {requestingAddon && (
            <div className="space-y-4">
              <div className="bg-white/10 rounded-xl p-4">
                <div className="flex justify-between">
                  <span className="text-white font-medium">{requestingAddon.name}</span>
                  <span className="font-bold" style={{ color: djPrimaryColor }}>${requestingAddon.price}</span>
                </div>
                {requestingAddon.description && (
                  <p className="text-white/50 text-sm mt-2">{requestingAddon.description}</p>
                )}
              </div>
              <div>
                <Label className="text-white/70">Notes (optional)</Label>
                <textarea
                  value={addonRequestNotes}
                  onChange={(e) => setAddonRequestNotes(e.target.value)}
                  placeholder="Any special requests?"
                  className="w-full mt-1 p-3 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 min-h-[80px]"
                />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setRequestingAddon(null)} className="border-white/20 text-white">
                  Cancel
                </Button>
                <Button
                  onClick={() => handleRequestAddon(requestingAddon)}
                  disabled={submittingAddonRequest}
                  style={{ backgroundColor: djPrimaryColor }}
                  className="text-white"
                  data-testid="submit-addon-request-btn"
                >
                  {submittingAddonRequest ? 'Submitting...' : 'Submit Request'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contract View Dialog */}
      <Dialog open={showViewContractDialog} onOpenChange={setShowViewContractDialog}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contract</DialogTitle>
          </DialogHeader>
          {selectedContract && (
            <div className="space-y-4">
              {selectedContract.content && (
                <div className="prose prose-invert max-w-none">
                  <div dangerouslySetInnerHTML={{ __html: selectedContract.content }} />
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => downloadContract(selectedContract)} className="border-white/20 text-white">
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
                {!selectedContract.client_signed_at && selectedContract.status !== 'fully_signed' && (
                  <Button
                    onClick={() => { setShowViewContractDialog(false); openSignContractDialog(selectedContract); }}
                    style={{ backgroundColor: djPrimaryColor }}
                    className="text-white"
                  >
                    Sign Contract
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contract Sign Dialog */}
      <Dialog open={showSignContractDialog} onOpenChange={setShowSignContractDialog}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Sign Contract</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/70">Type your full legal name</Label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Your full name"
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
            <div className="bg-white/10 rounded-xl p-4 text-center">
              <p className="text-white/50 text-sm mb-2">Signature Preview</p>
              <p className="text-2xl italic" style={{ fontFamily: 'cursive', color: djPrimaryColor }}>
                {signerName || 'Your Name'}
              </p>
            </div>
            <p className="text-white/50 text-xs">
              By signing, you agree to the terms and conditions outlined in this contract.
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowSignContractDialog(false)} className="border-white/20 text-white">
                Cancel
              </Button>
              <Button
                onClick={handleSignContract}
                disabled={signingContract || !signerName.trim()}
                style={{ backgroundColor: '#22c55e' }}
                className="text-white"
              >
                {signingContract ? 'Signing...' : 'Sign Contract'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Apple Music Import Dialog */}
      <Dialog open={showAppleMusicImport} onOpenChange={setShowAppleMusicImport}>
        <DialogContent className="bg-gray-900 border-white/10 max-w-lg" data-testid="apple-music-import-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Music className="w-5 h-5" style={{ color: djPrimaryColor }} />
              Import from Apple Music
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {!appleMusicImportResult ? (
              <>
                <div className="space-y-2">
                  <Label className="text-white">Apple Music Playlist URL</Label>
                  <Input
                    value={appleMusicUrl}
                    onChange={(e) => setAppleMusicUrl(e.target.value)}
                    placeholder="https://music.apple.com/us/playlist/..."
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                    data-testid="apple-music-url-input"
                  />
                  <p className="text-xs text-white/50">
                    Paste a shared Apple Music playlist link. Open Apple Music → Playlist → Share → Copy Link
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Add songs to</Label>
                  <Select value={appleMusicTargetPlaylist} onValueChange={setAppleMusicTargetPlaylist}>
                    <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="apple-music-target-select">
                      <SelectValue placeholder="Select destination..." />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-white/20">
                      <SelectItem value="new" className="text-white hover:bg-white/10">Create New Playlist</SelectItem>
                      {playlists?.map(p => (
                        <SelectItem key={p.id} value={p.id} className="text-white hover:bg-white/10">
                          {p.name} ({p.songs?.length || 0} songs)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <DialogFooter className="mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setShowAppleMusicImport(false)}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAppleMusicImport}
                    disabled={importingAppleMusic || !appleMusicUrl.trim()}
                    className="text-white"
                    style={{ backgroundColor: djPrimaryColor }}
                    data-testid="import-apple-music-submit-btn"
                  >
                    {importingAppleMusic ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Import Playlist
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <>
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <h3 className="text-white font-medium mb-2">{appleMusicImportResult.playlist_name}</h3>
                  <p className="text-white/60 text-sm mb-3">{appleMusicImportResult.song_count} songs found</p>
                  
                  <div className="max-h-60 overflow-y-auto space-y-2">
                    {appleMusicImportResult.songs.slice(0, 20).map((song, idx) => (
                      <div key={idx} className="flex items-center gap-3 py-1.5 px-2 rounded bg-white/5">
                        <span className="text-white/40 text-xs w-5">{idx + 1}</span>
                        <span className="text-white text-sm">{song.title}</span>
                      </div>
                    ))}
                    {appleMusicImportResult.song_count > 20 && (
                      <p className="text-white/40 text-xs text-center pt-2">
                        ... and {appleMusicImportResult.song_count - 20} more songs
                      </p>
                    )}
                  </div>
                </div>
                
                <DialogFooter className="mt-6">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setAppleMusicImportResult(null);
                      setAppleMusicUrl('');
                    }}
                    className="border-white/20 text-white hover:bg-white/10"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={confirmAppleMusicImport}
                    disabled={savingPlaylist}
                    className="text-white"
                    style={{ backgroundColor: djPrimaryColor }}
                    data-testid="confirm-apple-music-import-btn"
                  >
                    {savingPlaylist ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Create Playlist
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* YouTube Import Dialog */}
      <Dialog open={showYouTubeImport} onOpenChange={setShowYouTubeImport}>
        <DialogContent className="bg-gray-900 border-white/10 max-w-lg" data-testid="youtube-import-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <svg className="w-5 h-5 text-red-500" viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.19a3 3 0 0 0-2.11-2.13C19.55 3.5 12 3.5 12 3.5s-7.55 0-9.39.56A3 3 0 0 0 .5 6.19 31.5 31.5 0 0 0 0 12a31.5 31.5 0 0 0 .5 5.81 3 3 0 0 0 2.11 2.13c1.84.56 9.39.56 9.39.56s7.55 0 9.39-.56a3 3 0 0 0 2.11-2.13A31.5 31.5 0 0 0 24 12a31.5 31.5 0 0 0-.5-5.81zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/></svg>
              Import from YouTube
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-white">YouTube Playlist URL</Label>
              <Input
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/playlist?list=..."
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                data-testid="youtube-url-input"
              />
              <p className="text-xs text-white/50">
                Paste a YouTube playlist URL. Song names and artists will be extracted from video titles.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Add songs to</Label>
              <Select value={youtubeTargetPlaylist} onValueChange={setYoutubeTargetPlaylist}>
                <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="youtube-target-select">
                  <SelectValue placeholder="Select destination..." />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-white/20">
                  <SelectItem value="new" className="text-white hover:bg-white/10">Create New Playlist</SelectItem>
                  {playlists?.map(p => (
                    <SelectItem key={p.id} value={p.id} className="text-white hover:bg-white/10">
                      {p.name} ({p.songs?.length || 0} songs)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => setShowYouTubeImport(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleYouTubeImport}
                disabled={importingYouTube || !youtubeUrl.trim()}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="import-youtube-submit-btn"
              >
                {importingYouTube ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Import Playlist
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Playlist Generator Dialog */}
      <Dialog open={showAIPlaylistDialog} onOpenChange={setShowAIPlaylistDialog}>
        <DialogContent className="bg-gray-900 border-white/10 max-w-lg" data-testid="ai-playlist-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-400" />
              AI Playlist Generator
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
              <p className="text-purple-300 text-sm">
                ✨ Describe the kind of music you want and our AI will create a playlist of 50 songs for you to start with. You can then edit, add, or remove songs.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Playlist Name *</Label>
              <Input
                value={aiPlaylistName}
                onChange={(e) => setAiPlaylistName(e.target.value)}
                placeholder="e.g., Reception Dance, Cocktail Hour, Dinner Music"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                data-testid="ai-playlist-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Describe the music you want *</Label>
              <textarea
                value={aiPlaylistCriteria}
                onChange={(e) => setAiPlaylistCriteria(e.target.value)}
                placeholder="e.g., Hip hop and disco songs from the 80s and 90s, upbeat party music that will get everyone dancing. Mix of classics and modern hits."
                className="w-full min-h-[120px] bg-white/10 border border-white/20 rounded-xl p-3 text-white placeholder:text-white/40 resize-none"
                data-testid="ai-playlist-criteria-input"
              />
              <p className="text-xs text-white/50">
                Be specific! Include genres, decades, mood, and any specific artists or songs you like.
              </p>
            </div>
            
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <p className="text-white/60 text-sm">
                <strong className="text-white">Example prompts:</strong>
              </p>
              <ul className="text-white/50 text-sm mt-2 space-y-1">
                <li>• "Romantic slow songs for first dance and father-daughter dance"</li>
                <li>• "Latin and reggaeton mix for a high-energy reception"</li>
                <li>• "Classic rock and Motown for a cocktail hour"</li>
                <li>• "Country music hits for a barn wedding"</li>
              </ul>
            </div>
            
            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => setShowAIPlaylistDialog(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={generateAIPlaylist}
                disabled={generatingAIPlaylist || !aiPlaylistCriteria.trim() || !aiPlaylistName.trim()}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="generate-ai-playlist-btn"
              >
                {generatingAIPlaylist ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating 50 songs...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate Playlist
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Guest Request Setup Dialog */}
      <Dialog open={showGuestRequestSetup} onOpenChange={setShowGuestRequestSetup}>
        <DialogContent className="bg-gray-900 border-white/10 max-w-md" data-testid="guest-request-setup-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-pink-400" />
              Guest Song Requests
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-4">
              <p className="text-pink-300 text-sm">
                Let your guests request songs for your event! They'll need a password to access the request page.
              </p>
            </div>

            <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
              <div>
                <p className="text-white font-medium">Enable Guest Requests</p>
                <p className="text-white/50 text-sm">Allow guests to submit song requests</p>
              </div>
              <button
                onClick={() => setGuestRequestSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
                className={`w-12 h-6 rounded-full transition-all ${
                  guestRequestSettings.enabled ? 'bg-green-500' : 'bg-white/20'
                }`}
                data-testid="toggle-guest-requests"
              >
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${
                  guestRequestSettings.enabled ? 'translate-x-6' : 'translate-x-0.5'
                }`} />
              </button>
            </div>
            
            {guestRequestSettings.enabled && (
              <div className="space-y-2">
                <Label className="text-white">Access Password *</Label>
                <Input
                  type="text"
                  value={guestRequestSettings.password}
                  onChange={(e) => setGuestRequestSettings(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Create a simple password for guests"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="guest-request-password-input"
                />
                <p className="text-white/40 text-xs">
                  Share this password with your guests (e.g., include it in your wedding website or invitations)
                </p>
              </div>
            )}

            {guestRequestSettings.enabled && guestRequestSettings.link_code && (
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-white/50 text-xs mb-2">Guest Request Link</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-sm text-white bg-black/30 px-3 py-2 rounded-lg overflow-x-auto">
                    {getBaseUrl()}/guest-requests/{guestRequestSettings.link_code}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(`${getBaseUrl()}/guest-requests/${guestRequestSettings.link_code}`);
                      toast.success('Link copied!');
                    }}
                    className="text-white/60 hover:text-white"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
            
            <DialogFooter className="mt-6">
              <Button
                variant="outline"
                onClick={() => setShowGuestRequestSetup(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={saveGuestRequestSettings}
                disabled={savingGuestSettings || (guestRequestSettings.enabled && !guestRequestSettings.password.trim())}
                className="bg-pink-600 hover:bg-pink-700 text-white"
                data-testid="save-guest-settings-btn"
              >
                {savingGuestSettings ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Save Settings
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Approve Guest Request Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="bg-gray-900 border-white/10 max-w-md" data-testid="approve-guest-request-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Music className="w-5 h-5 text-green-400" />
              Add to Playlist
            </DialogTitle>
          </DialogHeader>
          
          {requestToApprove && (
            <div className="space-y-4 mt-4">
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <p className="text-white font-medium">{requestToApprove.song_title}</p>
                <p className="text-white/60 text-sm">{requestToApprove.artist}</p>
                <p className="text-white/40 text-xs mt-2">
                  Requested by {requestToApprove.guest_name}
                  {requestToApprove.upvote_count > 0 && (
                    <span className="ml-2 text-pink-400">
                      • {requestToApprove.upvote_count} upvote{requestToApprove.upvote_count !== 1 ? 's' : ''}
                    </span>
                  )}
                </p>
              </div>

              <div className="space-y-2">
                <Label className="text-white">Select Playlist</Label>
                <Select
                  value={selectedApprovePlaylist}
                  onValueChange={setSelectedApprovePlaylist}
                >
                  <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="select-playlist-trigger">
                    <SelectValue placeholder="Choose a playlist..." />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-white/20">
                    {playlists && playlists.map(pl => (
                      <SelectItem 
                        key={pl.id} 
                        value={pl.id}
                        className="text-white hover:bg-white/10"
                      >
                        {pl.name} ({pl.songs?.length || 0} songs)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <DialogFooter className="mt-6">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowApproveDialog(false);
                    setRequestToApprove(null);
                  }}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const playlist = playlists.find(p => p.id === selectedApprovePlaylist);
                    if (playlist) {
                      approveGuestRequest(selectedApprovePlaylist, playlist.name);
                    }
                  }}
                  disabled={!selectedApprovePlaylist}
                  className="bg-green-600 hover:bg-green-700 text-white"
                  data-testid="confirm-approve-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Add to Playlist
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vendor Email Dialog */}
      <Dialog open={showVendorEmailDialog} onOpenChange={setShowVendorEmailDialog}>
        <DialogContent className="bg-gray-900 border-white/10 max-w-lg" data-testid="vendor-email-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5" style={{ color: djPrimaryColor }} />
              Email {selectedVendor?.name}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="bg-white/5 rounded-xl p-3 border border-white/10">
              <p className="text-xs text-white/50 mb-1">Subject (auto-generated)</p>
              <p className="text-sm text-white">
                {booking?.event_type} - {booking?.client_name} - {booking?.event_date}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Your Message</Label>
              <textarea
                value={vendorEmailMessage}
                onChange={(e) => setVendorEmailMessage(e.target.value)}
                placeholder="Write your message to the vendor..."
                className="w-full min-h-[150px] bg-white/10 border border-white/20 rounded-xl p-3 text-white placeholder:text-white/40 resize-none"
                data-testid="vendor-email-message"
              />
            </div>
            
            <p className="text-xs text-white/40">
              Your contact info ({booking?.client_email}) will be included so the vendor can reply directly to you.
            </p>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowVendorEmailDialog(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendVendorEmail}
                disabled={sendingVendorEmail || !vendorEmailMessage.trim()}
                className="text-white"
                style={{ backgroundColor: djPrimaryColor }}
                data-testid="send-vendor-email-btn"
              >
                {sendingVendorEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Vendor Dialog */}
      <Dialog open={showAddVendorDialog} onOpenChange={setShowAddVendorDialog}>
        <DialogContent className="bg-gray-900 border-white/10 max-w-lg" data-testid="add-vendor-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Building2 className="w-5 h-5" style={{ color: djPrimaryColor }} />
              Add New Vendor
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Vendor Name *</Label>
                <Input
                  value={newVendor.name}
                  onChange={(e) => setNewVendor({ ...newVendor, name: e.target.value })}
                  placeholder="e.g., John's Photography"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="new-vendor-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Category</Label>
                <Select 
                  value={newVendor.category} 
                  onValueChange={(v) => setNewVendor({ ...newVendor, category: v })}
                >
                  <SelectTrigger className="bg-white/10 border-white/20 text-white" data-testid="new-vendor-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-800 border-white/20">
                    <SelectItem value="Photographer" className="text-white hover:bg-white/10">Photographer</SelectItem>
                    <SelectItem value="Videographer" className="text-white hover:bg-white/10">Videographer</SelectItem>
                    <SelectItem value="Florist" className="text-white hover:bg-white/10">Florist</SelectItem>
                    <SelectItem value="Caterer" className="text-white hover:bg-white/10">Caterer</SelectItem>
                    <SelectItem value="Cake/Baker" className="text-white hover:bg-white/10">Cake/Baker</SelectItem>
                    <SelectItem value="Officiant" className="text-white hover:bg-white/10">Officiant</SelectItem>
                    <SelectItem value="Planner" className="text-white hover:bg-white/10">Planner</SelectItem>
                    <SelectItem value="Transportation" className="text-white hover:bg-white/10">Transportation</SelectItem>
                    <SelectItem value="Hair/Makeup" className="text-white hover:bg-white/10">Hair/Makeup</SelectItem>
                    <SelectItem value="Rentals" className="text-white hover:bg-white/10">Rentals</SelectItem>
                    <SelectItem value="Lighting" className="text-white hover:bg-white/10">Lighting</SelectItem>
                    <SelectItem value="Photo Booth" className="text-white hover:bg-white/10">Photo Booth</SelectItem>
                    <SelectItem value="Other" className="text-white hover:bg-white/10">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Contact Name</Label>
              <Input
                value={newVendor.contact_name}
                onChange={(e) => setNewVendor({ ...newVendor, contact_name: e.target.value })}
                placeholder="Contact person's name"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                data-testid="new-vendor-contact"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-white">Email *</Label>
                <Input
                  type="email"
                  value={newVendor.email}
                  onChange={(e) => setNewVendor({ ...newVendor, email: e.target.value })}
                  placeholder="vendor@email.com"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="new-vendor-email"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-white">Phone *</Label>
                <Input
                  type="tel"
                  value={newVendor.phone}
                  onChange={(e) => setNewVendor({ ...newVendor, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                  data-testid="new-vendor-phone"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white">Notes (optional)</Label>
              <textarea
                value={newVendor.notes}
                onChange={(e) => setNewVendor({ ...newVendor, notes: e.target.value })}
                placeholder="Any additional notes..."
                className="w-full min-h-[80px] bg-white/10 border border-white/20 rounded-xl p-3 text-white placeholder:text-white/40 resize-none"
                data-testid="new-vendor-notes"
              />
            </div>
            
            <p className="text-xs text-white/40">* Required fields</p>
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowAddVendorDialog(false)}
                className="border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddVendor}
                disabled={addingVendor || !newVendor.name.trim() || !newVendor.email.trim() || !newVendor.phone.trim()}
                className="text-white"
                style={{ backgroundColor: djPrimaryColor }}
                data-testid="submit-add-vendor-btn"
              >
                {addingVendor ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Vendor
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Playlist Dialog */}
      <Dialog open={showPlaylistDialog} onOpenChange={(open) => {
        if (!open && playlistAutoSaveTimerRef.current) {
          clearTimeout(playlistAutoSaveTimerRef.current);
          playlistAutoSaveTimerRef.current = null;
        }
        setShowPlaylistDialog(open);
      }}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>{editingPlaylist ? 'Edit Playlist' : 'Create Playlist'}</DialogTitle>
              {/* Auto-save status */}
              {editingPlaylist && (
                <div className="flex items-center gap-2 text-sm">
                  {playlistAutoSaveStatus === 'saving' && (
                    <span className="text-amber-400 flex items-center gap-1">
                      <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                      Saving...
                    </span>
                  )}
                  {playlistAutoSaveStatus === 'saved' && (
                    <span className="text-green-400 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Saved
                    </span>
                  )}
                  {playlistAutoSaveStatus === 'error' && (
                    <span className="text-red-400 flex items-center gap-1">
                      <X className="w-4 h-4" />
                      Save failed
                    </span>
                  )}
                  {playlistAutoSaveStatus === '' && editingPlaylist && (
                    <span className="text-white/40 text-xs">Auto-saves as you type</span>
                  )}
                </div>
              )}
            </div>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-white/70">Playlist Name</Label>
              <Input
                value={playlistName}
                onChange={(e) => {
                  setPlaylistName(e.target.value);
                  if (editingPlaylist) triggerPlaylistAutoSave();
                }}
                placeholder="e.g., Must Play Songs"
                className="bg-white/10 border-white/20 text-white mt-1"
              />
            </div>
            
            {/* Play in Order Toggle */}
            <div className="flex items-center justify-between bg-white/5 rounded-xl p-3">
              <div className="flex items-center gap-2">
                <ListOrdered className="w-4 h-4 text-blue-400" />
                <div>
                  <p className="text-white text-sm font-medium">Retain & Play in Order</p>
                  <p className="text-white/50 text-xs">DJ will play songs in this specific order</p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={playInOrder}
                onClick={() => {
                  setPlayInOrder(!playInOrder);
                  if (editingPlaylist) triggerPlaylistAutoSave();
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${playInOrder ? 'bg-blue-500' : 'bg-white/20'}`}
                data-testid="play-in-order-toggle"
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${playInOrder ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            
            {/* Spotify Import */}
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-white/70 text-sm mb-2">Import from Spotify</p>
              <div className="flex gap-2">
                <Input
                  value={spotifyUrl}
                  onChange={(e) => setSpotifyUrl(e.target.value)}
                  placeholder="Paste Spotify playlist URL"
                  className="flex-1 bg-white/10 border-white/20 text-white"
                />
                <Button
                  onClick={importFromSpotify}
                  disabled={importingSpotify}
                  variant="outline"
                  className="border-white/20 text-white"
                >
                  {importingSpotify ? 'Importing...' : 'Import'}
                </Button>
              </div>
            </div>

            {/* YouTube Search - Add individual songs */}
            <div className="bg-white/5 rounded-xl p-4">
              <p className="text-white/70 text-sm mb-2">Search YouTube for Songs</p>
              <div className="flex gap-2">
                <Input
                  value={youtubeSearchQuery}
                  onChange={(e) => setYoutubeSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchYouTube()}
                  placeholder="Search for a song on YouTube..."
                  className="flex-1 bg-white/10 border-white/20 text-white"
                  data-testid="youtube-search-input"
                />
                <Button
                  onClick={searchYouTube}
                  disabled={searchingYouTube}
                  variant="outline"
                  className="border-red-500/30 text-red-300 hover:bg-red-500/10"
                  data-testid="youtube-search-btn"
                >
                  {searchingYouTube ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </div>
              {youtubeSearchResults.length > 0 && (
                <div className="mt-3 max-h-48 overflow-y-auto space-y-1">
                  {youtubeSearchResults.map((result, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                      {result.thumbnail && (
                        <img src={result.thumbnail} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{result.title}</p>
                        <p className="text-white/50 text-xs truncate">{result.artist}</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <a href={result.youtube_url} target="_blank" rel="noopener noreferrer" className="text-red-400 hover:text-red-300">
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => addYouTubeSongToPlaylist(result)}
                          className="h-7 px-2 text-green-400 hover:text-green-300 hover:bg-green-500/10"
                          data-testid={`youtube-add-song-${idx}`}
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Designation Legend */}
            <div className="flex flex-wrap gap-3 text-xs">
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span className="text-white/60">Must Play</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                <span className="text-white/60">Play if Possible</span>
              </span>
              <span className="flex items-center gap-1">
                <span className="w-3 h-3 rounded-full bg-red-500"></span>
                <span className="text-white/60">Do Not Play</span>
              </span>
            </div>
            
            {/* Songs List */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-white/70">Songs {playInOrder && <span className="text-blue-400 text-xs ml-2">(drag to reorder)</span>}</Label>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={openAddSongModal}
                  className="border-white/20 text-white/70 hover:text-white"
                  data-testid="add-song-top-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Song
                </Button>
              </div>
              <div className="space-y-4">
                {playInOrder ? (
                  /* Ordered mode with drag-and-drop */
                  <DndContext
                    sensors={playlistDndSensors}
                    collisionDetection={closestCenter}
                    onDragEnd={(event) => {
                      const { active, over } = event;
                      if (active.id !== over?.id) {
                        const oldIndex = playlistSongs.findIndex((_, i) => `song-${i}` === active.id);
                        const newIndex = playlistSongs.findIndex((_, i) => `song-${i}` === over.id);
                        setPlaylistSongs(arrayMove(playlistSongs, oldIndex, newIndex));
                        if (editingPlaylist) triggerPlaylistAutoSave();
                      }
                    }}
                  >
                    <SortableContext items={playlistSongs.map((_, i) => `song-${i}`)} strategy={verticalListSortingStrategy}>
                      {playlistSongs.map((song, idx) => (
                        <SortablePlaylistSong
                          key={`song-${idx}`}
                          id={`song-${idx}`}
                          song={song}
                          idx={idx}
                          orderNum={idx + 1}
                          playlistSongs={playlistSongs}
                          setPlaylistSongs={setPlaylistSongs}
                          editingPlaylist={editingPlaylist}
                          triggerPlaylistAutoSave={triggerPlaylistAutoSave}
                          searchingSongIndex={searchingSongIndex}
                          showSongSearchResults={showSongSearchResults}
                          setShowSongSearchResults={setShowSongSearchResults}
                          setSearchingSongIndex={setSearchingSongIndex}
                          songSearchQuery={songSearchQuery}
                          setSongSearchQuery={setSongSearchQuery}
                          searchSongs={searchSongs}
                          songSearchResults={songSearchResults}
                          searchingSongs={searchingSongs}
                          selectSongFromSearch={selectSongFromSearch}
                          openAddSongModal={openAddSongModal}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                /* Grouped by Designation mode */
                (() => {
                  const designationOrder = ['must_play', 'play_if_possible', 'do_not_play'];
                  const designationLabels = {
                    'must_play': 'Must Play',
                    'play_if_possible': 'Play if Possible',
                    'do_not_play': 'Do Not Play'
                  };
                  const designationColors = {
                    'must_play': 'bg-green-500',
                    'play_if_possible': 'bg-amber-500',
                    'do_not_play': 'bg-red-500'
                  };
                  
                  // Sort songs by designation, then artist, then title
                  const sortedSongs = [...playlistSongs].map((s, origIdx) => ({ ...s, origIdx }));
                  sortedSongs.sort((a, b) => {
                    const desigA = designationOrder.indexOf(a.designation || 'must_play');
                    const desigB = designationOrder.indexOf(b.designation || 'must_play');
                    if (desigA !== desigB) return desigA - desigB;
                    const artistCmp = (a.artist || '').toLowerCase().localeCompare((b.artist || '').toLowerCase());
                    if (artistCmp !== 0) return artistCmp;
                    return (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase());
                  });
                  
                  // Group by designation
                  const groups = {};
                  sortedSongs.forEach(song => {
                    const desig = song.designation || 'must_play';
                    if (!groups[desig]) groups[desig] = [];
                    groups[desig].push(song);
                  });
                  
                  return designationOrder.map(desig => {
                    const songs = groups[desig] || [];
                    if (songs.length === 0) return null;
                    
                    return (
                      <div key={desig} className="space-y-2">
                        {/* Group Header */}
                        <div className="flex items-center gap-2 py-2 border-b border-white/10">
                          <div className={`w-3 h-3 rounded-full ${designationColors[desig]}`}></div>
                          <span className="text-white/70 font-medium text-sm">{designationLabels[desig]}</span>
                          <span className="text-white/40 text-xs">({songs.length} songs)</span>
                        </div>
                        
                        {/* Songs in this group */}
                        {songs.map((song) => {
                          const idx = song.origIdx;
                          return (
                            <PlaylistSongRow
                              key={idx}
                              song={song}
                              idx={idx}
                              playlistSongs={playlistSongs}
                              setPlaylistSongs={setPlaylistSongs}
                              editingPlaylist={editingPlaylist}
                              triggerPlaylistAutoSave={triggerPlaylistAutoSave}
                              searchingSongIndex={searchingSongIndex}
                              showSongSearchResults={showSongSearchResults}
                              setShowSongSearchResults={setShowSongSearchResults}
                              setSearchingSongIndex={setSearchingSongIndex}
                              songSearchQuery={songSearchQuery}
                              setSongSearchQuery={setSongSearchQuery}
                              searchSongs={searchSongs}
                              songSearchResults={songSearchResults}
                              searchingSongs={searchingSongs}
                              selectSongFromSearch={selectSongFromSearch}
                            />
                          );
                        })}
                      </div>
                    );
                  });
                })()
                )}
                
                <Button
                  variant="outline"
                  onClick={openAddSongModal}
                  className="w-full border-dashed border-white/20 text-white/70"
                  data-testid="add-song-bottom-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Song
                </Button>
              </div>
            </div>
            
            <DialogFooter className="flex items-center justify-between sm:justify-between">
              {editingPlaylist && <p className="text-white/40 text-xs hidden sm:block">Changes are automatically saved</p>}
              <div className="flex gap-2 ml-auto">
                <Button variant="outline" onClick={() => setShowPlaylistDialog(false)} className="border-white/20 text-white">
                  {editingPlaylist ? 'Close' : 'Cancel'}
                </Button>
                <Button
                  onClick={savePlaylist}
                  disabled={savingPlaylist}
                  style={{ backgroundColor: djPrimaryColor }}
                  className="text-white"
                >
                  {savingPlaylist ? 'Saving...' : (editingPlaylist ? 'Save & Close' : 'Create Playlist')}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Song Modal */}
      <Dialog open={showAddSongModal} onOpenChange={(open) => {
        if (!open) {
          setModalSearchQuery('');
          setModalSearchResults([]);
          setShowModalSearchResults(false);
        }
        setShowAddSongModal(open);
      }}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Music className="w-5 h-5" style={{ color: djPrimaryColor }} />
              Add Song
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {/* Song Search */}
            <div className="space-y-2 relative">
              <Label className="text-white/70 flex items-center gap-2">
                <Search className="w-4 h-4" />
                Search for a song
              </Label>
              <Input
                value={modalSearchQuery}
                onChange={(e) => searchSongsForModal(e.target.value)}
                placeholder="Type to search songs..."
                className="bg-white/10 border-white/20 text-white"
                data-testid="add-song-search-input"
                autoFocus
              />
              {/* Search Results Dropdown */}
              {showModalSearchResults && (
                <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-slate-800 border border-white/20 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                  {modalSearching ? (
                    <p className="p-3 text-white/50 text-sm">Searching...</p>
                  ) : modalSearchResults.length > 0 ? (
                    modalSearchResults.map((result, idx) => (
                      <button
                        key={idx}
                        onClick={() => selectSongFromModalSearch(result)}
                        className="w-full text-left p-3 hover:bg-white/10 transition-colors border-b border-white/10 last:border-b-0"
                        data-testid={`search-result-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-medium text-sm">{result.title}</p>
                            <p className="text-white/50 text-xs">{result.artist}</p>
                          </div>
                          {result.in_library && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
                              In Library
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  ) : modalSearchQuery.length >= 2 ? (
                    <p className="p-3 text-white/50 text-sm">No results found</p>
                  ) : null}
                </div>
              )}
              <p className="text-white/40 text-xs">Or enter manually below</p>
            </div>

            <div className="border-t border-white/10 pt-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-white/70">Song Title <span className="text-red-400">*</span></Label>
                <Input
                  value={newSong.title}
                  onChange={(e) => setNewSong({ ...newSong, title: e.target.value })}
                  placeholder="Enter song title"
                  className="bg-white/10 border-white/20 text-white"
                  data-testid="add-song-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-white/70">Artist</Label>
                <Input
                  value={newSong.artist}
                  onChange={(e) => setNewSong({ ...newSong, artist: e.target.value })}
                  placeholder="Enter artist name"
                  className="bg-white/10 border-white/20 text-white"
                  data-testid="add-song-artist-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white/70">Designation <span className="text-red-400">*</span></Label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setNewSong({ ...newSong, designation: 'must_play' })}
                  className={`p-3 rounded-lg border transition-all ${
                    newSong.designation === 'must_play'
                      ? 'border-green-500 bg-green-500/20 text-green-400'
                      : 'border-white/20 text-white/60 hover:border-white/40'
                  }`}
                  data-testid="add-song-must-play-btn"
                >
                  <div className="flex flex-col items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    <span className="text-xs font-medium">Must Play</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setNewSong({ ...newSong, designation: 'play_if_possible' })}
                  className={`p-3 rounded-lg border transition-all ${
                    newSong.designation === 'play_if_possible'
                      ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                      : 'border-white/20 text-white/60 hover:border-white/40'
                  }`}
                  data-testid="add-song-play-if-possible-btn"
                >
                  <div className="flex flex-col items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                    <span className="text-xs font-medium">Play if Possible</span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setNewSong({ ...newSong, designation: 'do_not_play' })}
                  className={`p-3 rounded-lg border transition-all ${
                    newSong.designation === 'do_not_play'
                      ? 'border-red-500 bg-red-500/20 text-red-400'
                      : 'border-white/20 text-white/60 hover:border-white/40'
                  }`}
                  data-testid="add-song-do-not-play-btn"
                >
                  <div className="flex flex-col items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    <span className="text-xs font-medium">Do Not Play</span>
                  </div>
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white/70">Comment (optional)</Label>
              <Input
                value={newSong.comment}
                onChange={(e) => setNewSong({ ...newSong, comment: e.target.value })}
                placeholder="e.g., 'For first dance', 'Client's favorite'"
                className="bg-white/10 border-white/20 text-white"
                data-testid="add-song-comment-input"
              />
            </div>
            
            <DialogFooter className="pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowAddSongModal(false)}
                className="border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAddSongFromModal}
                disabled={!newSong.title.trim() || !newSong.designation}
                style={{ backgroundColor: djPrimaryColor }}
                className="text-white"
                data-testid="add-song-confirm-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Song
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Form Dialog */}
      <Dialog open={showFormDialog} onOpenChange={(open) => {
        if (!open && autoSaveTimerRef.current) {
          // If closing, trigger immediate save if there are pending changes
          clearTimeout(autoSaveTimerRef.current);
          autoSaveTimerRef.current = null;
          if (selectedForm && data?.booking && !currentFormLocked) {
            axios.post(`${API}/client-portal/${token}/form-response`, {
              form_id: selectedForm.id,
              booking_id: data.booking.id,
              responses: formResponses
            }).catch(() => {});
          }
        }
        setShowFormDialog(open);
      }}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>{selectedForm?.name}</DialogTitle>
              {/* Auto-save status indicator */}
              {!currentFormLocked && (
                <div className="flex items-center gap-2 text-sm">
                  {autoSaveStatus === 'saving' && (
                    <span className="text-amber-400 flex items-center gap-1">
                      <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                      Saving...
                    </span>
                  )}
                  {autoSaveStatus === 'saved' && (
                    <span className="text-green-400 flex items-center gap-1">
                      <Check className="w-4 h-4" />
                      Saved
                    </span>
                  )}
                  {autoSaveStatus === 'error' && (
                    <span className="text-red-400 flex items-center gap-1">
                      <X className="w-4 h-4" />
                      Save failed
                    </span>
                  )}
                  {autoSaveStatus === '' && (
                    <span className="text-white/40 text-xs">Auto-saves as you type</span>
                  )}
                </div>
              )}
            </div>
          </DialogHeader>
          {selectedForm && (
            <div className="space-y-6">
              {selectedForm.fields?.map((field, idx) => (
                <div key={idx} className="space-y-2">
                  {field.type !== 'section' && field.type !== 'comment' && (
                    <Label className="text-white/70">
                      {field.label}
                      {field.required && <span className="text-red-400 ml-1">*</span>}
                    </Label>
                  )}
                  {renderFormField(field, currentFormLocked, formResponses, handleFormInputChange, handleRepeaterChange, addRepeaterRow, removeRepeaterRow, reorderRepeaterRows, handleColumnChange, djPrimaryColor)}
                </div>
              ))}
              
              {!currentFormLocked && (
                <DialogFooter className="flex items-center justify-between sm:justify-between">
                  <p className="text-white/40 text-xs hidden sm:block">Changes are automatically saved</p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowFormDialog(false)} className="border-white/20 text-white">
                      Close
                    </Button>
                    <Button
                      onClick={submitFormResponse}
                      disabled={submittingForm}
                      style={{ backgroundColor: djPrimaryColor }}
                      className="text-white"
                    >
                      {submittingForm ? 'Saving...' : 'Save & Close'}
                    </Button>
                  </div>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Invoice View Dialog */}
      <Dialog open={showInvoiceDialog} onOpenChange={setShowInvoiceDialog}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Invoice {selectedInvoice?.invoice_number}</DialogTitle>
          </DialogHeader>
          {loadingInvoice ? (
            <p className="text-white/50 text-center py-8">Loading...</p>
          ) : invoiceDetails ? (
            <div className="space-y-4">
              <div className="prose prose-invert max-w-none">
                <div dangerouslySetInnerHTML={{ __html: invoiceDetails.html_content || '' }} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => downloadInvoice(selectedInvoice)} className="border-white/20 text-white">
                  <Download className="w-4 h-4 mr-2" />
                  Download PDF
                </Button>
              </DialogFooter>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showChangePasswordDialog} onOpenChange={setShowChangePasswordDialog}>
        <DialogContent className="bg-slate-900 border-white/20 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5" />
              Change Password
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-white/70">Current Password</Label>
              <div className="relative">
                <Input
                  type="password"
                  value={passwordForm.current}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
                  placeholder="Enter current password"
                  className="bg-white/10 border-white/20 text-white pr-10"
                  data-testid="current-password-input"
                />
                <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-white/70">New Password</Label>
              <Input
                type="password"
                value={passwordForm.new}
                onChange={(e) => setPasswordForm({ ...passwordForm, new: e.target.value })}
                placeholder="Enter new password (min. 6 characters)"
                className="bg-white/10 border-white/20 text-white"
                data-testid="new-password-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-white/70">Confirm New Password</Label>
              <Input
                type="password"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
                placeholder="Re-enter new password"
                className="bg-white/10 border-white/20 text-white"
                data-testid="confirm-password-input"
              />
              {passwordForm.new && passwordForm.confirm && passwordForm.new !== passwordForm.confirm && (
                <p className="text-red-400 text-sm">Passwords do not match</p>
              )}
            </div>
            
            <DialogFooter className="pt-4">
              <Button 
                variant="outline" 
                onClick={() => { setShowChangePasswordDialog(false); setPasswordForm({ current: '', new: '', confirm: '' }); }}
                className="border-white/20 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !passwordForm.current || !passwordForm.new || !passwordForm.confirm || passwordForm.new !== passwordForm.confirm}
                style={{ backgroundColor: djPrimaryColor }}
                className="text-white"
                data-testid="submit-password-change-btn"
              >
                {changingPassword ? 'Updating...' : 'Update Password'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Playlist Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent className="bg-slate-900 border-white/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Playlist</AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              Are you sure you want to delete "{playlistToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel 
              className="bg-transparent border-white/20 text-white hover:bg-white/10"
              onClick={() => {
                setShowDeleteConfirm(false);
                setPlaylistToDelete(null);
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              onClick={confirmDeletePlaylist}
              disabled={deletingPlaylist}
              data-testid="confirm-delete-playlist-btn"
            >
              {deletingPlaylist ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

